import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import { Button, Col, Input, Label, Row, FormGroup, Nav, NavItem, NavLink,TabContent, TabPane, } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { ConceptNode, NetworkConceptsFilter, loadAxesForConcept, selectAxesForConcept, clearAxesForConcepts } from '../../redux/AccountsSlice';
import { bulkReassignColumnDimension, DimensionReference, DimensionReferenceInput, createDimensionReferenceFromInput } from '../../redux/AfrSlice';
import { Concept, isSummableDatatype, getPublicName, getPublicAxisName } from '../../redux/ConceptSlice';
import { Node, selectUnitHierarchy } from '../../redux/EntitiesSlice';
import { isAdmin } from '../../redux/MiscSlice';
import {
    AccountRootDependencyInput, AccountDependencyInput, AccountDependencySetInput, AccountScope,
    addAccountDependencyChange, addDimensionalScopeChange, addEntityScopeChange,
    ConceptInput, DimensionalConceptRefInput, getTaxonomyChanges, RelationshipInput,
    TaxonomyChangesState, clearChanges, anyChanges
} from '../../redux/TaxonomyChangesSlice';
import {
    AccountRootDependency, AccountDependency, DtsConcept, getSelectedTaxonomyConcept,
    resetTaxonomyConceptState
} from '../../redux/TaxonomyConceptSlice';
import { clearSelectedReference, loadAllTaxonomyReferences } from '../../redux/TaxonomyReferenceSlice';
import { ActiveTaxonomyVersionInfo, TaxonomyVersionInfo, getReferenceForVersion, selectCurrentVersion, selectAvailableVersions } from '../../redux/TaxonomyVersionSlice';
import enums from '../../utils/enums';
import dateHelper from '../../utils/dateHelper';
import utilityHelper from '../../utils/utilityHelper';
import { SelectableListItem } from './BypassRequest';
import Switch from "react-switch";
import { Tree, NodeApi } from "react-arborist";
import TreeNode from '../layout/Treeview/TreeNode';
import { ITreeObj } from '../layout/Treeview/types';
import '../layout/Treeview/styles.css';
import Autosize from "react-virtualized-auto-sizer";
import { version } from '@babel/core';
import { NumericFormat } from 'react-number-format';
import DatePicker from "react-datepicker";
import Select from 'react-select';


type Props = {
    variant: string,
    className: string,
    onClick: any
}

export interface RulesEditorProps {
    currentConcept: DtsConcept | null;
    accountTree: Array<ConceptNode>;
    handleUnitScopeChange: (currentNode: any, selectedNodes: any) => boolean;
    conceptChanges: ConceptInput[];
    relationshipChanges: RelationshipInput[];
    versionInfo: any;
    handleDependencyAccountList: (accountTree: ConceptNode[] | null, selections: string[],
        versionInfo: any, conceptChanges: ConceptInput[], relationshipChanges: RelationshipInput[]) => Array<SelectableListItem>;
}
interface DependencyTreeItem {
    metadata: any | null;
    name: string;
    children: DependencyTreeItem[];
}
interface DependencyTreeItemA extends ITreeObj {
    children: DependencyTreeItemA[] | null;
    editing: boolean;
    changed: boolean;
    remove: boolean;
}
interface DependencyTableData {
    id: string;
    dependency: EditableAccountDependency | EditableAccountSubDependency;
    accounts: string;
    relationship: string;
    value: string;
    name: string;
    _children: DependencyTableData[] | null;
}
// NOTE - This is a variation of the interface defined in ConceptSlice. Here we add a "code" attribute to use in the table for conciseness
export interface ConceptDisplayRef {
    conceptName: string;
    conceptId: string;
    code: string;
    afrYear: number;
    datatype: string;
}
interface EditableAccountSubDependency {
    editable: boolean,
    year: number | null;
    id: string;
    name: string | null;
    target: ConceptDisplayRef | null;
    dependencies: EditableAccountSubDependency[];
    relationship: string;
    value: number | null;
    booleanValue: boolean | null;
    fallback: number | null;
    booleanFallback: boolean | null;
    isSequence: boolean | null;
    test: string | null;
    changed: boolean;
    remove: boolean;
    dimensionQualifiers: DimensionReference[] | null;
    dimensionDisQualifiers: DimensionReference[] | null;
    dimensionsToMatch: DimensionReference[] | null;
    decimals: number | null;
    isRule: boolean;
    root: EditableAccountDependency | null;
}
interface EditableAccountDependency {
    ruleId: string | null,
    editable: boolean,
    year: number | null;
    id: string;
    name: string | null;
    target: ConceptDisplayRef | null;
    dependencies: EditableAccountSubDependency[] | null;
    preconditions: EditableAccountSubDependency[] | null;
    relationship: string;
    value: number | null;
    booleanValue: boolean | null;
    fallback: number | null;
    booleanFallback: boolean | null;
    isSequence: boolean | null;
    test: string | null;
    message: string | null;
    changed: boolean;
    remove: boolean;
    dimensionQualifiers: DimensionReference[] | null;
    dimensionDisQualifiers: DimensionReference[] | null;
    dimensionsToMatch: DimensionReference[] | null;
    isRule: boolean;
    newName: string | null;
    isNew: boolean;
    deprecationDate: Date | null;
    decimals: number | null;
}

export interface StyledSelectableListItem {
    label: string;
    value: string;
    disabled: boolean;
    checked: boolean;
    className: string;
    children: Array<StyledSelectableListItem>;
}

const AccountDependenciesEditor = (props: RulesEditorProps) => {
    const dispatch = useAppDispatch();
    const isAdminUser: boolean = useAppSelector(isAdmin);
    var versionedConcept: DtsConcept | null = useAppSelector(getSelectedTaxonomyConcept);
    var changes: TaxonomyChangesState | null = useAppSelector(getTaxonomyChanges);
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    var unitHierarchy: Node | null = useAppSelector(selectUnitHierarchy);
    var concept: Concept | null = versionedConcept ? versionedConcept.concept : null;
    var axesForConcept: ConceptNode[] = useAppSelector(selectAxesForConcept);
    const availableVersionsSelector: Array<TaxonomyVersionInfo> | null = useAppSelector(selectAvailableVersions);

    const [activeTab, setActiveTab] = useState("current");
    const [versionYear, setVersionYear] = useState(versionInfo ? versionInfo.year : 2023);
    const [scopeChanges, setScopeChanges] = useState<string[]>([]);
    const [accountId, setAccountId] = useState<string>((versionedConcept && versionedConcept.concept) ? versionedConcept.concept.id : "")
    const [depAccounts, setDepAccounts] = useState<string>("");
    const [depRelationship, setDepRelationship] = useState<string>(enums.dependencyRelationship.NONE);
    const [depValue, setDepValue] = useState<string>("");
    const [depBooleanValue, setDepBooleanValue] = useState<boolean | null>(null);
    const [depTest, setDepTest] = useState<string>("");
    const [depRuleId, setDepRuleId] = useState<string>("");
    const [depName, setDepName] = useState<string>("");
    const [depMessage, setDepMessage] = useState<string>("");
    const [depUseFallback, setDepUseFallback] = useState<boolean>(false);
    const [depFallback, setDepFallback] = useState<string>("");
    const [depBooleanFallback, setDepBooleanFallback] = useState<boolean | null>(null);
    const [depIsSequence, setDepIsSequence] = useState<string>("");
    const [depChanges, setDepChanges] = useState<boolean>(false);
    const [selectedDependency, setSelectedDependency] = useState<EditableAccountDependency | EditableAccountSubDependency | null>(null);
    //   const [selectedDependency, setSelectedDependency] = useState<string | null>(null);
    const [selectedDependencyIsRoot, setSelectedDependencyIsRoot] = useState<boolean>(true);
    const [selectedRuleIsDeprecated, setSelectedRuleIsDeprecated] = useState<boolean>(false);
    const [canMakeRootDependencyEditable, setCanMakeRootDependencyEditable] = useState<boolean>(false);
    const [treeModel, setTreeModel] = useState<DependencyTreeItemA[]>([]); //useState <INode<any>[]>([])
    const [priorTreeModel, setPriorTreeModel] = useState<DependencyTreeItemA[]>([]); //useState <INode<any>[]>([])
    const [deprecationDate, setDeprecationDate] = React.useState<Date | null>(null);
    const [decimals, setDecimals] = React.useState<number | null>(null);
    const [ruleEditText, setRuleEditText] = React.useState<string>("Edit");
    const [depRemoveText, setDepRemoveText] = React.useState<string>("Remove Dependency");
    const [taxonomyIsDirty, setTaxonomyIsDirty] = useState(anyChanges(changes));

    const getFiscalYears = function () {
        const d = new Date();
        let year = d.getFullYear();
        const maxYear = year + 1;
        const minYear = versionYear + 1;
        var years = [{ value: 0, label: "none" }];
        if (minYear < maxYear) {
            for (let i = minYear; i <= maxYear; i++) {
                years.push({ value: i, label: i.toString() });
            }
        }
        return years;
    }
    const toggle = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
        if (tab === "current") {
            setRuleEditText("Edit");
        } else {
            setRuleEditText("Make Editable Copy for " + versionYear);
        }
        clearDependencySelection();
    }

    const activeClass = (tab: string) => {
        return (tab === activeTab) ? "active" : "";
    }

    const convertFloatTextToPercentText = function (value: string | null): string {
        if (!value) {
            return "";
        }
        return "" + Math.round(parseFloat(value) * 1000000) / 10000;
    }
    const convertPercentTextToFloatText = function (value: string | null): string {
        if (!value || value.length === 0) {
            return "";
        }
        return "" + parseFloat(value) / 100;
    }
    const findConceptNodeRecursive = function (cn: ConceptNode, accountId: string): ConceptNode | null {
        if (cn.concept && cn.concept.id === accountId) {
            return cn;
        }
        if (!cn.children) {
            return null;
        }
        var cnResult: ConceptNode | null;
        for (var cnChild of cn.children) {
            cnResult = findConceptNodeRecursive(cnChild, accountId);
            if (cnResult) {
                return cnResult;
            }
        }
        return null;
    }
    const findConceptNode = function (accountId: string): ConceptNode | null {
        var foundNode: ConceptNode | null = null;
        if (!props.accountTree) {
            return null;
        }
        for (var root of props.accountTree) {
            foundNode = findConceptNodeRecursive(root, accountId);
            if (foundNode) {
                return foundNode;
            }
        }
        return foundNode;
    }
    const getRelationship = function (rel: string): string {
        var result: string = enums.dependencyRelationship.NONE;
        if (rel && rel.length > 0) {
            result = rel;
        }
        return result;
    }
    const getTargetDatatype = function (selDep: any): string {
        if (!selDep || !selDep.target) {
            return "string";
        }
        return (selDep.target.datatype);
    }
    const createConceptDisplayRefFromId = function (accountId: string, year: number): ConceptDisplayRef {
        var cn: ConceptNode | null = findConceptNode(accountId);
        if (!cn) {
            var notFound: ConceptDisplayRef = {
                conceptId: accountId,
                conceptName: accountId,
                code: "no code",
                afrYear: year,
                datatype: "string"
            };
            return notFound;
        }
        var result: ConceptDisplayRef = {
            conceptId: accountId,
            conceptName: cn.concept.name,
            code: cn.concept.code,
            afrYear: year,
            datatype: cn.concept.datatype
        }
        return result;
    }
    const createTargetRef = function (dep: AccountDependency, year: number): ConceptDisplayRef | null {
        if (!dep.target) {
            return null;
        }
        return {
            conceptId: dep.target.id,
            conceptName: dep.target.name,
            code: dep.target.code,
            afrYear: year,
            datatype: dep.target.datatype
        }
    }
    const createEditableSubDependency = function (dep: AccountDependency, year: number): EditableAccountSubDependency {
        var deps: EditableAccountSubDependency[] = [];
        if (dep.dependencies) {
            dep.dependencies.map(d => deps.push(createEditableSubDependency(d, year)));
        }
        var result: EditableAccountSubDependency = {
            editable: false,
            year: year,
            id: utilityHelper.generateGuid(),
            name: dep.name,
            target: createTargetRef(dep, year),
            dependencies: deps,
            relationship: dep.relationship,
            value: dep.value,
            booleanValue: dep.booleanValue,
            fallback: dep.fallback,
            booleanFallback: dep.booleanFallback,
            isSequence: dep.isSequence,
            test: dep.test,
            changed: false,
            remove: false,
            dimensionQualifiers: dep.dimensionQualifiers,
            dimensionDisQualifiers: dep.dimensionDisQualifiers,
            dimensionsToMatch: dep.dimensionsToMatch,
            decimals: dep.decimals,
            isRule: false,
            root: null
        }
        return result;

    }
    const createEditableDependency = function (dep: AccountRootDependency, year: number): EditableAccountDependency {
        var deps: EditableAccountSubDependency[] = [];
        dep.dependencies.map(d => deps.push(createEditableSubDependency(d, year)));
        var pres: EditableAccountSubDependency[] = [];
        if (dep.preconditions) {
            dep.preconditions.map(p => pres.push(createEditableSubDependency(p, year)));
        }
        var depDate: Date | null = null;
        if (dep.deprecationDate) {
            depDate = dateHelper.convertToDate(dep.deprecationDate);
        }
        var result: EditableAccountDependency = {
            ruleId: dep.ruleId,
            editable: false,
            year: year,
            name: dep.name,
            target: createTargetRef(dep, year),
            dependencies: deps,
            preconditions: pres,
            relationship: dep.relationship,
            value: dep.value,
            booleanValue: dep.booleanValue,
            fallback: dep.fallback,
            booleanFallback: dep.booleanFallback,
            isSequence: dep.isSequence,
            test: dep.test,
            message: dep.message,
            id: utilityHelper.generateGuid(),
            changed: false,
            remove: false,
            dimensionQualifiers: dep.dimensionQualifiers,
            dimensionDisQualifiers: dep.dimensionDisQualifiers,
            dimensionsToMatch: dep.dimensionsToMatch,
            isRule: true,
            newName: null,
            isNew: false,
            deprecationDate: depDate,
            decimals: dep.decimals
        }
        updateDependencyRoots(result);
        return result;
    }
    const createDimensionReferenceList = function (drInputList: DimensionReferenceInput[] | null): DimensionReference[] {
        var results: DimensionReference[] = [];
        if (drInputList) {
            drInputList.map(dri => {
                results.push(createDimensionReferenceFromInput(dri));
            });
        }
        return results;
    }
    const setDependencyRoots = function (root: EditableAccountDependency, dep: EditableAccountSubDependency) {
        dep.root = root;
        if (dep.dependencies) {
            dep.dependencies.map(d => setDependencyRoots(root, d));
        }
    }
    const updateDependencyRoots = function (root: EditableAccountDependency) {
        if (root.dependencies) {
            root.dependencies.map(d => setDependencyRoots(root, d));
        }
        if (root.preconditions) {
            root.preconditions.map(p => setDependencyRoots(root, p));
        }
    }
    const createEditableSubDependencyFromChange = function (dep: AccountDependencyInput, year: number): EditableAccountSubDependency {
        var newDeps: EditableAccountSubDependency[] = [];
        if (dep.recursiveDependencies) {
            newDeps = dep.recursiveDependencies.map(subdep => createEditableSubDependencyFromChange(subdep, year));
        }
        var result: EditableAccountSubDependency = {
            editable: true,
            year: year,
            id: utilityHelper.generateGuid(),
            name: dep.name,
            relationship: dep.relationship,
            value: dep.value,
            booleanValue: dep.booleanValue,
            fallback: dep.fallback,
            booleanFallback: dep.booleanFallback,
            isSequence: dep.isSequence,
            test: dep.test,
            changed: false,
            remove: false,
            target: dep.conceptId ? createConceptDisplayRefFromId(dep.conceptId, year) : null,
            dependencies: newDeps,
            dimensionQualifiers: createDimensionReferenceList(dep.dimensionQualifiers),
            dimensionDisQualifiers: createDimensionReferenceList(dep.dimensionDisQualifiers),
            dimensionsToMatch: createDimensionReferenceList(dep.dimensionsToMatch),
            decimals: dep.decimals,
            isRule: false,
            root: null
        }
        return result;
    }
    const createEditableDependencyFromChange = function (dep: AccountRootDependencyInput, year: number): EditableAccountDependency {
        var newDeps: EditableAccountSubDependency[] = [];
        if (dep.recursiveDependencies) {
            newDeps = dep.recursiveDependencies.map(subdep => createEditableSubDependencyFromChange(subdep, year));
        }
        var newPre: EditableAccountSubDependency[] = [];
        if (dep.preconditions) {
            newPre = dep.preconditions.map(subdep => createEditableSubDependencyFromChange(subdep, year));
        }
        var result: EditableAccountDependency = {
            ruleId: dep.ruleId,
            editable: true,
            year: year,
            name: dep.name,
            relationship: dep.relationship,
            value: dep.value,
            booleanValue: dep.booleanValue,
            fallback: dep.fallback,
            booleanFallback: dep.booleanFallback,
            isSequence: dep.isSequence,
            test: dep.test,
            id: utilityHelper.generateGuid(),
            changed: false,
            remove: dep.remove ? dep.remove : false,
            target: dep.conceptId ? createConceptDisplayRefFromId(dep.conceptId, year) : null,
            dependencies: newDeps,
            preconditions: newPre,
            message: dep.message,
            dimensionQualifiers: createDimensionReferenceList(dep.dimensionQualifiers),
            dimensionDisQualifiers: createDimensionReferenceList(dep.dimensionDisQualifiers),
            dimensionsToMatch: createDimensionReferenceList(dep.dimensionsToMatch),
            isRule: true,
            newName: dep.newName,
            isNew: false,
            deprecationDate: dep.deprecationDate ? dateHelper.convertToDate(dep.deprecationDate) : null,
            decimals: dep.decimals
        }
        updateDependencyRoots(result);
        return result;
    }
    const getDependencyYear = function (dep: EditableAccountDependency | EditableAccountSubDependency | null) {
        if (!dep) {
            return 0;
        }
        if (dep.remove) {
            return dep.year + " (remove)"
        }
        if (dep.editable) {
            return dep.year + " (edit)"
        }
        return dep.year;
    }
    const selectedRuleIsEditable = function (): boolean {
        if (!selectedDependency || !selectedDependency.isRule || !versionInfo) {
            return false;
        }
        var rule: EditableAccountDependency = selectedDependency as EditableAccountDependency;
        if (rule.year === versionInfo.year) {
            return true;
        }
        return false;
    }
    const selectedRuleIsRemovable = function (): boolean {
        if (!selectedDependency || !selectedDependency.isRule || !versionInfo) {
            return false;
        }
        var rule: EditableAccountDependency = selectedDependency as EditableAccountDependency;
        if (rule.year !== versionInfo.year) {
            return false;
        }
        if (selectedDependency.remove) {
            return false;
        }
        return true;
    }
    const getMatchText = function (dep: EditableAccountDependency | EditableAccountSubDependency) {
        if (!dep.dimensionsToMatch) {
            return "";
        }
        return dep.dimensionsToMatch.map(dim => dim.axis.name).join(", ");
    }
    const getQualifiersText = function (dep: EditableAccountDependency | EditableAccountSubDependency) {
        if (!dep.dimensionQualifiers) {
            return "";
        }
        return dep.dimensionQualifiers.map(dim => dim.axis.name + (dim.member ? "#" + dim.member.name : "")).join(", ");
    }

    const getDisQualifiersText = function (dep: EditableAccountDependency | EditableAccountSubDependency) {
        if (!dep.dimensionDisQualifiers) {
            return "";
        }
        return dep.dimensionDisQualifiers.map(dim => dim.axis.name + (dim.member ? "#" + dim.member.name : "")).join(", ");
    }
    const getDependencyRuleName = function (dep: AccountRootDependency): string {
        if (dep.name) {
            return dep.name;
        }
        return "unknown";
    }

    const getDependencyChangeRuleName = function (dep: AccountRootDependencyInput): string {
        if (dep.name) {
            return dep.name;
        }
        return "unknown";
    }

    const initializeDependencySet = function (account: DtsConcept | null) {
        if (!account || !account.concept) {
            setDependencySet([]);
            return;
        }
        if (!versionInfo) {
            setDependencySet([]);
            return;
        }
        var accId = account.concept.id;
        var taxYear = versionInfo.year;
        var depSet: EditableAccountDependency[] = [];
        if (changes && changes.accountDependencyChanges && changes.accountDependencyChanges.hasOwnProperty(accId)) {
            var existingChanges: AccountDependencySetInput = changes.accountDependencyChanges[accId];
            var depMap: any = {};
            if (account.dependencies) {
                account.dependencies.map(dep => depMap[getDependencyRuleName(dep)] = createEditableDependency(dep, dep.year ? dep.year : taxYear));
            }
            existingChanges.dependencies.map(dep =>
                depMap[getDependencyChangeRuleName(dep)] = createEditableDependencyFromChange(dep, taxYear)
            );
            Object.keys(depMap).map(k => depSet.push(depMap[k]));
        } else {
            if (account.dependencies) {
                account.dependencies.map(dep => depSet.push(createEditableDependency(dep, dep.year ? dep.year : taxYear)));
            }
        }
        setDependencySet(depSet);
    }
    const updateSubDependency = function (dep: EditableAccountSubDependency, depUpdate: EditableAccountSubDependency): EditableAccountSubDependency {
        if (!dep.id) {
            return depUpdate;
        }
        if (depUpdate.id === dep.id) {
            return depUpdate;
        } else {
            return dep;
        }
    }
    const updateDependency = function (dep: EditableAccountDependency, depUpdate: EditableAccountDependency | EditableAccountSubDependency): EditableAccountDependency {
        if (!dep || !dep.id) {
            return dep;
        }
        if (depUpdate.id === dep.id) {
            return depUpdate as EditableAccountDependency;
        }
        if (depUpdate.isRule) {
            return dep;
        }
        var subDep = depUpdate as EditableAccountSubDependency;
        // depUpdate must be a sub dependency
        if (dep.preconditions) {
            var newPre: EditableAccountSubDependency[] = [];
            dep.preconditions.map(sub => newPre.push(updateSubDependency(sub, subDep)));
            dep.preconditions = newPre;
        }
        if (dep.dependencies) {
            var newDeps: EditableAccountSubDependency[] = [];
            dep.dependencies.map(sub => newDeps.push(updateSubDependency(sub, subDep)));
            dep.dependencies = newDeps;
        }
        return dep;
    }

    const updateDependencySet = function (depUpdate: EditableAccountDependency | EditableAccountSubDependency): EditableAccountDependency[] {
        var result: EditableAccountDependency[] = [];
        dependencySet.map((rootDep: EditableAccountDependency) => result.push(updateDependency(rootDep, depUpdate)));
        //        dependencySet.map((rootDep: EditableAccountDependency) => result.push(rootDep));
        result.map(rule => updateDependencyRoots(rule));
        setDependencySet(result);
        return result;
    }
    const [dependencySet, setDependencySet] = useState<EditableAccountDependency[]>([]);

    const getNodeIcon = function (concept: Concept | null): string {
        if (!concept) {
            return "";
        }
        if (concept.code < "2") {
            return ""; // county
        }
        if (concept.code < "3") {
            return ""; // city
        }
        if (concept.code < "5") {
            return ""; // special district
        }
        if (concept.code < "9") {
            return ""; // other entity
        }
        return ""; // organizing node
    }
    const addNode = function (parent: StyledSelectableListItem | null, n: Node, selIdMap: any): StyledSelectableListItem | null {
        var nodeLabel = n.concept ? n.concept.name : n.name;
        var nvalue = n.concept ? n.concept.id : n.id;
        const isSelected: boolean = selIdMap.hasOwnProperty(nvalue);
        var listItem: StyledSelectableListItem = {
            label: nodeLabel,
            value: nvalue,
            disabled: false,
            checked: isSelected,
            className: getNodeIcon(concept),
            children: []
        }
        if (parent) {
            parent.children.push(listItem);
        }
        if (n.outgoing) {
            n.outgoing.map(e => addNode(listItem, e.to, selIdMap));
        }
        return listItem;
    }
    const addConceptNode = function (parent: StyledSelectableListItem | null, n: ConceptNode, selIdMap: any): StyledSelectableListItem | null {
        var nodeLabel = n.concept ? n.concept.name : "na";
        var nvalue = n.concept ? n.concept.id : "na";
        const isSelected: boolean = selIdMap.hasOwnProperty(nvalue);
        var listItem: StyledSelectableListItem = {
            label: nodeLabel,
            value: nvalue,
            disabled: false,
            checked: isSelected,
            className: getNodeIcon(concept),
            children: []
        }
        if (parent) {
            parent.children.push(listItem);
        }
        if (n.children) {
            n.children.map(e => addConceptNode(listItem, e, selIdMap));
        }
        return listItem;
    }
    const addRootNode = function (alist: Array<StyledSelectableListItem>, root: StyledSelectableListItem | null) {
        if (root) {
            alist.push(root);
        }
    }
    const getUnitsList = function (unitHierarchy: Node | null, selectedConcept: DtsConcept | null): StyledSelectableListItem[] {
        var alist: Array<StyledSelectableListItem> = [];
        if (unitHierarchy && unitHierarchy.outgoing) {
            var selectedIdMap: any = {};
            if (selectedConcept && selectedConcept.concept) {
                // Check changes first 
                var selId = selectedConcept.concept.id;
                if (changes && changes.entityScopeChanges && changes.entityScopeChanges.hasOwnProperty(selId)) {
                    const mapping: string[] = changes.entityScopeChanges[selId];
                    mapping.map(scid => selectedIdMap[scid] = selectedConcept.concept);
                } else if (selectedConcept.entityScope) {
                    selectedConcept.entityScope.map(es => selectedIdMap[es.id] = es);
                }
            }
            unitHierarchy.outgoing.map(e => addRootNode(alist, addNode(null, e.to, selectedIdMap)));
        }
        return alist;
    }
    const [unitsList, setUnitsList] = useState<StyledSelectableListItem[]>([]);

    const getDimensionsList = function (selectedConcept: DtsConcept | null): StyledSelectableListItem[] {
        var alist: Array<StyledSelectableListItem> = [];
        if (selectedConcept && selectedConcept.dimensionalBreakdowns) {
            var selectedIdMap: any = {};
            if (selectedConcept && selectedConcept.concept) {
                // Check changes first 
                var selId = selectedConcept.concept.id;
                if (changes && changes.dimensionalScopeChanges && changes.dimensionalScopeChanges.hasOwnProperty(selId)) {
                    const mapping: string[] = changes.dimensionalScopeChanges[selId];
                    mapping.map(scid => selectedIdMap[scid] = selectedConcept.concept);
                } else if (selectedConcept.dimensionalScope) {
                    selectedConcept.dimensionalScope.map(es => selectedIdMap[es.member != null ? es.member.id : ""] = es);
                }
            }
            selectedConcept.dimensionalBreakdowns.map(e => addRootNode(alist, addConceptNode(null, e, selectedIdMap)));
        }
        return alist;
    }
    const [dimensionsList, setDimensionsList] = useState<StyledSelectableListItem[]>([]);

    const getDependentsList = function (selectedDependency: EditableAccountDependency | EditableAccountSubDependency | null): SelectableListItem[] {
        var alist: Array<SelectableListItem> = [];
        if (selectedDependency) {
            var selections: string[] = [];
            if (selectedDependency.target && selectedDependency.target.conceptId) {
                selections.push(selectedDependency.target.conceptId + "^" + selectedDependency.target.datatype);
            }
            // TO REMOVE            selectedDependency.accounts.map(dep => selections.push(dep.conceptId));
            alist = props.handleDependencyAccountList(
                props.accountTree,
                selections,
                props.versionInfo,
                props.conceptChanges,
                props.relationshipChanges
            );
            //            props.accountTree.map(e => addRootNode(alist, addConceptNode(null, e, selectedIdMap)));
        }
        return alist;
    }
    const [dependentsList, setDependentsList] = useState<SelectableListItem[]>([]);

    const getDimensionMatchList = function (selectedDependency: EditableAccountDependency): SelectableListItem[] {
        var alist: Array<SelectableListItem> = [];
        if (selectedDependency && axesForConcept) {
            var axisMap: any = {};
            if (selectedDependency.dimensionsToMatch) {
                selectedDependency.dimensionsToMatch.map(dri => {
                    axisMap[dri.axis.id] = dri;
                });
            }
            axesForConcept.map(cn => {
                var item: SelectableListItem = {
                    label: cn.concept.name,
                    value: cn.concept.id,
                    disabled: false,
                    checked: axisMap.hasOwnProperty(cn.concept.id),
                    children: []
                }
                alist.push(item);
            });
        }
        return alist;
    }
    const getDimensionMatchListForSub = function (selectedDependency: EditableAccountSubDependency): SelectableListItem[] {
        var alist: Array<SelectableListItem> = [];
        if (selectedDependency && axesForConcept) {
            var axisMap: any = {};
            if (selectedDependency.dimensionsToMatch) {
                selectedDependency.dimensionsToMatch.map(dri => {
                    axisMap[dri.axis.id] = dri;
                });
            }
            axesForConcept.map(cn => {
                var item: SelectableListItem = {
                    label: cn.concept.name,
                    value: cn.concept.id,
                    disabled: false,
                    checked: axisMap.hasOwnProperty(cn.concept.id),
                    children: []
                }
                alist.push(item);
            });
        }
        return alist;
    }
    const [dimensionMatchList, setDimensionMatchList] = useState<SelectableListItem[]>([]);

    const addDimensionChildren = function (assignedMap: any, axisId: string, parentNode: ConceptNode, items: SelectableListItem[]) {
        if (!parentNode.children || parentNode.children.length == 0) {
            return;
        }
        parentNode.children.map(cn => {
            var childItems: SelectableListItem[] = [];
            addDimensionChildren(assignedMap, axisId, cn, childItems);
            var key: string = axisId + '#' + cn.concept.id;
            var item: SelectableListItem = {
                label: cn.concept.name,
                value: key,
                disabled: false,
                checked: assignedMap.hasOwnProperty(key),
                children: childItems
            }
            items.push(item);
        }
        );

    }
    const getDimensionList = function (dimList: DimensionReference[] | null): SelectableListItem[] {
        var alist: Array<SelectableListItem> = [];
        var assignedMap: any = {};
        if (dimList) {
            dimList.map(dr => {
                var key: string = dr.axis.id + (dr.member ? '#' + dr.member.id : "");
                assignedMap[key] = dr;
            });
        }

        axesForConcept.map(cn => {
            var childItems: SelectableListItem[] = [];
            addDimensionChildren(assignedMap, cn.concept.id, cn, childItems);
            var item: SelectableListItem = {
                label: cn.concept.name,
                value: cn.concept.id,
                disabled: false,
                checked: assignedMap.hasOwnProperty(cn.concept.id),
                children: childItems
            }
            alist.push(item);
        });
        return alist;
    }
    const getDimensionQualifiersList = function (selectedDependency: EditableAccountDependency | EditableAccountSubDependency | null): SelectableListItem[] {
        if (selectedDependency && axesForConcept) {
            return getDimensionList(selectedDependency.dimensionQualifiers);
        }
        return [];
    }
    const getDimensionDisQualifiersList = function (selectedDependency: EditableAccountDependency | EditableAccountSubDependency | null): SelectableListItem[] {
        if (selectedDependency && axesForConcept) {
            return getDimensionList(selectedDependency.dimensionDisQualifiers);
        }
        return [];
    }
    const [dimensionQualifiersList, setDimensionQualifiersList] = useState<SelectableListItem[]>([]);
    const [dimensionDisQualifiersList, setDimensionDisQualifiersList] = useState<SelectableListItem[]>([]);

    const makeSubEditable = function (dep: EditableAccountSubDependency, taxYear: number) {
        if (dep.editable) {
            return;
        }
        if (dep.dependencies) {
            dep.dependencies.map(sub => makeSubEditable(sub, taxYear));
        }
        dep.year = taxYear;
        dep.editable = true;
    }
    const makeEditable = function (dep: EditableAccountDependency, taxYear: number) {
        if (dep.editable) {
            return;
        }
        if (dep.preconditions) {
            dep.preconditions.map(sub => makeSubEditable(sub, taxYear));
        }
        if (dep.dependencies) {
            dep.dependencies.map(sub => makeSubEditable(sub, taxYear));
        }
        dep.year = taxYear;
        dep.editable = true;
        updateDependencySet(dep);
        setCanMakeRootDependencyEditable(false);
    }
    const getConceptId = function (snvalue: string) {
        return snvalue.split("^")[0];
    }
    const ddhandleUnitChange = function (currentNode: any, selectedNodes: any) {
        if (!versionedConcept || !versionedConcept.concept) {
            return;
        }
        var sids: string[] = [];
        selectedNodes.map((sn: StyledSelectableListItem) => sids.push(getConceptId(sn.value)));
        const scopeChange: AccountScope = {
            accountId: versionedConcept.concept.id,
            scopeIds: sids
        }
        dispatch(addEntityScopeChange(scopeChange));
    }

    const ddhandleDimensionChange = function (currentNode: any, selectedNodes: any) {
        if (!versionedConcept || !versionedConcept.concept) {
            return;
        }
        var sids: string[] = [];
        selectedNodes.map((sn: StyledSelectableListItem) => sids.push(getConceptId(sn.value)));
        const scopeChange: AccountScope = {
            accountId: versionedConcept.concept.id,
            scopeIds: sids
        }
        dispatch(addDimensionalScopeChange(scopeChange));
    }

    const createConceptDisplayRef = function (sn: SelectableListItem): ConceptDisplayRef {
        var parts: string[] = sn.value.split("^");
        var dt = "string";
        if (parts.length > 1) {
            dt = parts[1];
        }
        var cdr: ConceptDisplayRef = {
            conceptName: sn.label,
            conceptId: parts[0],
            code: sn.label,
            afrYear: props.versionInfo.year,
            datatype: dt
        }
        return cdr;
    }
    const ddhandleDependentChange = function (currentNode: any, selectedNodes: any) {
        if (!selectedDependency) {
            return;
        }
        var deps: ConceptDisplayRef[] = [];
        selectedNodes.map((sn: SelectableListItem) => deps.push(createConceptDisplayRef(sn)));
        selectedDependency.target = deps.length > 0 ? deps[0] : null;
        selectedDependency.changed = true;
        var axesFilter: NetworkConceptsFilter = {
            taxonomyVersion: getReferenceForVersion(versionInfo, -1),
            nameFilter: selectedDependency.target ? selectedDependency.target.conceptId : "none"
        }
        updateDependencySet(selectedDependency);
        dispatch(loadAxesForConcept(axesFilter));
    }

    const handleUseFallbackChange = function (e: any) {
        if (!selectedDependency) {
            return;
        }
        setDepUseFallback(e.target.checked);
        selectedDependency.changed = true;
        selectedDependency.booleanFallback = null;
        if (e.target.checked && !targetIsAbstract()) {
            // For non-numeric and non-boolean default to 0 for fallback
            selectedDependency.fallback = 0;
        } else {
            selectedDependency.fallback = null;
        }
        updateDependencySet(selectedDependency);
    }

    const ddhandleDependencyItemChange = function (currentNode: any, selectedNodes: any) {
        if (!selectedDependency) {
            return;
        }
    }
    const createDimRefInputsFromSelection = function (selectedNodes: any[]) {
        var inputList: DimensionReferenceInput[] = [];
        selectedNodes.map((n) => {
            var parts: string[] = n.value.split('#');
            var dri: DimensionReferenceInput = {
                axisId: parts[0],
                memberId: parts.length > 1 ? parts[1] : null,
                typedContent: null
            };
            inputList.push(dri);
        })
        return inputList;
    }
    const ddhandleMatchChange = function (currentNode: any, selectedNodes: any[]) {
        if (!selectedDependency || !selectedDependency.editable) {
            return;
        }
        var inputList = createDimRefInputsFromSelection(selectedNodes);
        selectedDependency.dimensionsToMatch = createDimensionReferenceList(inputList);
        selectedDependency.changed = true;
        updateDependencySet(selectedDependency);
    }

    const ddhandleQualifiersChange = function (currentNode: any, selectedNodes: any[]) {
        if (!selectedDependency || !selectedDependency.editable) {
            return;
        }
        var inputList = createDimRefInputsFromSelection(selectedNodes);
        selectedDependency.dimensionQualifiers = createDimensionReferenceList(inputList);
        selectedDependency.changed = true;
        updateDependencySet(selectedDependency);
    }

    const ddhandleDisQualifiersChange = function (currentNode: any, selectedNodes: any[]) {
        if (!selectedDependency || !selectedDependency.editable) {
            return;
        }
        var inputList = createDimRefInputsFromSelection(selectedNodes);
        selectedDependency.dimensionDisQualifiers = createDimensionReferenceList(inputList);
        selectedDependency.changed = true;
        updateDependencySet(selectedDependency);
    }

    const redStyle = {
        color: 'red'
    }

    const getDependencyRef = function (dep: EditableAccountDependency | EditableAccountSubDependency): string {
        if (dep.name) {
            return dep.name;
        }
        if (dep.target) {
            if (dep.target.code) {
                return dep.target.code;
            }
            return dep.target.conceptName;
        }
        return "na";
    }

    const addDimRefToName = function (dimRep: DimensionReference, dimReps: string[], prefix : string) {
        var axisName = prefix + getPublicAxisName(dimRep.axis);
        if (dimRep.member) {
            dimReps.push(axisName + "#" + getPublicName(dimRep.member));
        } else {
            dimReps.push(axisName);
        }
    }
    const getSimpleOperatorReference = function (dep: EditableAccountSubDependency): string {
        var result: string = "";
        var simpleOperator: string | null = null;
        switch (dep.relationship) {
            case enums.dependencyRelationship.EQUALS:
                simpleOperator = "=";
                break;
            case enums.dependencyRelationship.GREATER_THAN:
                simpleOperator = ">";
                break;
            case enums.dependencyRelationship.GREATER_THAN_EQUAL:
                simpleOperator = ">=";
                break;
            case enums.dependencyRelationship.LESS_THAN:
                simpleOperator = "<";
                break;
            case enums.dependencyRelationship.LESS_THAN_EQUAL:
                simpleOperator = "<=";
                break;
            case enums.dependencyRelationship.NOT_EQUALS:
                simpleOperator = "<>";
                break;
        }
        if (simpleOperator) {
            result += " " + simpleOperator + " ";
            if (dep.value !== null) {
                result += dep.value;
            } else if (dep.booleanValue !== null) {
                result += dep.booleanValue;
            }
        }
        return result;
    }
    // handle the logic to add a new account
    const getFunctionalName = function (dep: EditableAccountDependency | EditableAccountSubDependency, name: string): string {
        var funcName: string = name;
        var dimReps: string[] = [];
        if (dep.dimensionQualifiers && dep.dimensionQualifiers.length > 0) {
            dep.dimensionQualifiers.map(dimRep => addDimRefToName(dimRep,dimReps, ""));
        }
        if (dep.dimensionDisQualifiers && dep.dimensionDisQualifiers.length > 0) {
            dep.dimensionDisQualifiers.map(dimRep => addDimRefToName(dimRep, dimReps, "~"));
        }
        if (dimReps.length > 0) {
            funcName += " (" + dimReps.join(", ") + ")";
        }
        if (dep.isSequence && dep.target) {
            if (isSummableDatatype(dep.target.datatype)) {
                return "sum(" + funcName + ") ";
            } else {
                return funcName + "[]";
            }
        }
        return funcName;
    }
    const getSubClause = function (dep: EditableAccountSubDependency): string {
        var result: string = "";
        if (dep.target) {
            result += " (";
            result += getFunctionalName(dep, getDependencyRef(dep));
            switch (dep.relationship) {
                case enums.dependencyRelationship.EXISTS_ALL:
                case enums.dependencyRelationship.EXISTS:
                    result += " exists";
                    break;

                case enums.dependencyRelationship.NOT_EXISTS:
                    result += " does not exist";
                    break;

                case enums.dependencyRelationship.EQUALS:
                case enums.dependencyRelationship.GREATER_THAN:
                case enums.dependencyRelationship.GREATER_THAN_EQUALS:
                case enums.dependencyRelationship.LESS_THAN:
                case enums.dependencyRelationship.LESS_THAN_EQUALS:
                case enums.dependencyRelationship.NOT_EQUALS:
                    result += " " + dep.relationship + " ";
                    if (dep.value !== null) {
                        result += dep.value;
                    } else if (dep.booleanValue !== null) {
                        result += dep.booleanValue;
                    }
                    break;
            }
            result += " ) ";
        } else {
            var depText: string = "";
            var depList: string[] = [];
            if (dep.dependencies && dep.dependencies.length > 0) {
                dep.dependencies.map(d => depList.push(getFunctionalName(d, getDependencyRef(d)) + getSimpleOperatorReference(d)));
                var dname = dep.name;
                switch (depList.length) {
                    case 0:
                        break;

                    case 1:
                        depText = depList[0];
                        break;

                    default:
                        depText = " " + dname + "(" + depList.join(", ") + ")";
                        break;
                }
            }
            if (dep.relationship) {
                switch (dep.relationship) {
                    case enums.dependencyRelationship.EXISTS_ALL:
                    case enums.dependencyRelationship.EXISTS:
                        result += "there exists all of " + depText
                        break;

                    case enums.dependencyRelationship.NOT_EXISTS:
                        result += "there does not exist  " + depText
                        break;

                    case enums.dependencyRelationship.EXISTS_ANY:
                        result += "there exists any of  " + depText
                        break;

                    case enums.dependencyRelationship.EXISTS_NONE:
                        result += "there exists none of " + depText
                        break;

                    case enums.dependencyRelationship.EXISTS_ONE:
                        result += "there exists one of " + depText
                        break;
                }
            }
        }
        return result;
    }
    const getDependencyExistenceRelationships = function (dep: EditableAccountDependency | EditableAccountSubDependency) {
        var result: string = " ";
        if (dep.dependencies && dep.dependencies.length > 0) {
            var depText: string = "";
            var depList: string[] = [];
            dep.dependencies.map(d => depList.push(getFunctionalName(d, getDependencyRef(d)) + getSimpleOperatorReference(d)));
            switch (depList.length) {
                case 0:
                    break;

                case 1:
                    depText = depList[0];
                    break;

                default:
                    depText = " (" + depList.join(", ") + ")";
                    break;
            }

            switch (dep.relationship) {
                case enums.dependencyRelationship.EXISTS_ALL:
                case enums.dependencyRelationship.EXISTS:
                    result += "requires existence of all " + depText
                    break;

                case enums.dependencyRelationship.NOT_EXISTS:
                    result += "requires non-existence of all " + depText
                    break;

                case enums.dependencyRelationship.EXISTS_ANY:
                    result += "requires existence of any " + depText
                    break;

                case enums.dependencyRelationship.EXISTS_NONE:
                    result += "requires existence of none " + depText
                    break;

                case enums.dependencyRelationship.EXISTS_ONE:
                    result += "requires existence of one " + depText
                    break;
            }

        } else {
            switch (dep.relationship) {
                case enums.dependencyRelationship.EXISTS_ALL:
                case enums.dependencyRelationship.EXISTS:
                    result += "must exist";
                    break;

                case enums.dependencyRelationship.NOT_EXISTS:
                    result += "must not exist";
                    break;

            }
            /*
            var preText: string | null = null;
            var depRoot = dep as EditableAccountDependency;
            if (depRoot.preconditions && depRoot.preconditions.length > 0) {
                result += " if ";
                var preList: string[] = [];
                depRoot.preconditions.map(pre => preList.push(getSubClause(pre as EditableAccountSubDependency)));
                switch (preList.length) {
                    case 0:
                        break;

                    case 1:
                        result += preList[0];
                        break;

                    default:
                        result += preList.join(" and ");
                        break;
                }
            }
            */
        }
        return result;
    }
    const getNameAndRelationship = function (dep: EditableAccountDependency | EditableAccountSubDependency, name: string): string {
        var result: string = "";
        var depRoot = dep as EditableAccountDependency;
        if (depRoot.preconditions && depRoot.preconditions.length > 0) {
            result += "If ";
            var preList: string[] = [];
            depRoot.preconditions.map(pre => preList.push(getSubClause(pre as EditableAccountSubDependency)));
            switch (preList.length) {
                case 0:
                    break;

                case 1:
                    result += preList[0];
                    break;

                default:
                    result += preList.join(" and ");
                    break;
            }
            result += " then ";
        }
        result += getFunctionalName(dep, name);
        var value: string = "";
        if (dep.value !== null) {
            value = "" + dep.value;
        } else if (dep.booleanValue !== null) {
            value = "" + dep.booleanValue;
        } else if (dep.dependencies && dep.dependencies.length > 0) {
            var drefs: string[] = [];
            dep.dependencies.map(d => drefs.push(getFunctionalName(d, d.name ? d.name : (d.target ? d.target.code : "unknown"))));
            if (dep.dependencies.length == 1) {
                value = drefs.join(", ");
            } else {
                value = " (" + drefs.join(", ") + ")";
            }
        }
        if (dep.relationship) {
            switch (dep.relationship) {
                case enums.dependencyRelationship.EQUALS:
                case enums.dependencyRelationship.GREATER_THAN:
                case enums.dependencyRelationship.GREATER_THAN_EQUAL:
                case enums.dependencyRelationship.LESS_THAN:
                case enums.dependencyRelationship.LESS_THAN_EQUAL:
                    result += " " + enums.dependencyRelationshipMapping[dep.relationship] + " " + value;
                    break;

                case enums.dependencyRelationship.TEST:
                    result += " ={ " + dep.test + " }";
                    break;

                case enums.dependencyRelationship.EXISTS:
                case enums.dependencyRelationship.NOT_EXISTS:
                case enums.dependencyRelationship.EXISTS_ALL:
                case enums.dependencyRelationship.EXISTS_ANY:
                case enums.dependencyRelationship.EXISTS_NONE:
                case enums.dependencyRelationship.EXISTS_ONE:
                    result += getDependencyExistenceRelationships(dep);
                    break;

            }
        }
        return result;
    }
    const getTargetRef = function (target: ConceptDisplayRef): string {
        if (!target) {
            return "unknown";
        }
        if (target.code && target.code.length > 0) {
            return target.code;
        }
        return target.conceptId;
    }
    const getSummary = function (dep: EditableAccountDependency | EditableAccountSubDependency): string {
        var result = "";
        if (dep.target) {
            result += getNameAndRelationship(dep, getTargetRef(dep.target));
        } else {
            result += getSubClause(dep as EditableAccountSubDependency);
        }
        return result;
    }
    const getDeprecationYear = function (dep: EditableAccountDependency): number | null {
        if (!dep || !dep.deprecationDate) {
            return null;
        }
        return dep.deprecationDate.getFullYear() + 1;
    }
    const getSummaryName = function (dep: EditableAccountDependency | EditableAccountSubDependency): string {
        var yearAndDep: string = "";
        if (dep.isRule) {
            yearAndDep += " (" + dep.year;
            var depYear: number | null = getDeprecationYear(dep as EditableAccountDependency);
            if (depYear) {
                yearAndDep += " deprecated " + depYear;
            }
            yearAndDep += ")";
        }
        if (dep.name && dep.name.length > 0) {
            var rid = "";
            var itemName = dep.name;
            if (dep.isRule) {
                var rootDep = dep as EditableAccountDependency;
                if (rootDep.ruleId && rootDep.ruleId.length > 0) {
                    rid = rootDep.ruleId + " ";
                }
                if (rootDep.newName && rootDep.newName.length > 0) {
                    itemName = rootDep.newName;    
                }
            }
            if (rid.length > 0 && itemName.length > 55) {
                itemName = itemName.substring(0,55) + "...";
            }
            return rid + itemName + yearAndDep;
        }
        if (dep.target && dep.target.conceptName) {
            return dep.target.conceptName + yearAndDep;
        }
        return "";
    }
    const getBooleanTextValue = function (bval: boolean | null): string {
        if (bval === null) {
            return "";
        }
        return bval + "";
    }
    const convertTextToBoolean = function (sval: string | null): boolean {
        var result: boolean = false;
        if (sval === null) {
            return result;
        }
        switch (sval.toLowerCase()) {
            case "y":
            case "yes":
            case "t":
            case "true":
            case "1":
            case "on":
                result = true;
                break;
        }
        return result;
    }
    const populateSubDependencyStates = function (dep: EditableAccountSubDependency) {
        var conceptIds: string[] = [];
        if (dep.target) {
            conceptIds.push(dep.target.code);
        }
        setDepAccounts(conceptIds.join(", "));
        setDepRelationship(getRelationship(dep.relationship));
        setDepValue((dep.value !== null) ? dep.value + "" : "");
        setDepBooleanValue(dep.booleanValue);
        setDepFallback(dep.fallback ? dep.fallback + "" : "");
        setDepBooleanFallback(dep.booleanFallback);
        setDepIsSequence(getBooleanTextValue(dep.isSequence));
        setDepTest(dep.test ? dep.test : "");
        setDepRuleId("");
        setDepName(dep.name ? dep.name : "");
        setDepMessage("");
        setDeprecationDate(null);
        setDecimals(dep.decimals);
        if( dep.root ) {
            setSelectedRuleIsDeprecated(dep.root.deprecationDate !== null);
        } else {
            setSelectedRuleIsDeprecated(false);
        }
        if (dep.name && dep.name.length > 0) {
            setDepRemoveText("Remove " + dep.name);
        } else {
            setDepRemoveText("Remove Dependency");
        }
        if (dep.fallback === null && dep.booleanFallback === null) {
            setDepUseFallback(false);
        } else {
            setDepUseFallback(true);
        }
    }
    const populateRootDependencyStates = function (dep: EditableAccountDependency) {
        var conceptIds: string[] = [];
        if (dep.target) {
            conceptIds.push(dep.target.code);
        }
        setDepAccounts(conceptIds.join(", "));
        setDepRelationship(getRelationship(dep.relationship));
        setDepValue((dep.value !== null) ? dep.value + "" : "");
        setDepBooleanValue(dep.booleanValue);
        setDepFallback(dep.fallback ? dep.fallback + "" : "");
        setDepBooleanFallback(dep.booleanFallback);
        setDepIsSequence(getBooleanTextValue(dep.isSequence));
        setDepTest(dep.test ? dep.test : "");
        if (dep.newName) {
            setDepName(dep.newName);
        } else {
            setDepName(dep.name ? dep.name : "");
        }
        setDepRuleId(dep.ruleId ? dep.ruleId : "");
        setDepMessage(dep.message ? dep.message : "");
        if (dep.booleanFallback === null && dep.fallback === null) {
            setDepUseFallback(false);
        } else {
            setDepUseFallback(true);
        }
        setDeprecationDate(dep.deprecationDate);
        setSelectedRuleIsDeprecated(dep.deprecationDate !== null);
        setDecimals(dep.decimals);
    }
    const clearDependencyStates = function () {
        setDepValue("");
        setDepBooleanValue(null);
        setDepFallback("");
        setDepBooleanFallback(null);
        setDepIsSequence("");
        setDepTest("");
        setDepAccounts("");
        setDepRelationship(enums.dependencyRelationship.NONE);
        setDepRuleId("");
        setDepName("");
        setDepMessage("");
        setDependentsList([]);
        setDimensionMatchList([]);
        setDeprecationDate(null);
        setDecimals(null);
    }
    const getDeprecateDateString = function(): string {
        if (!deprecationDate) {
            return "";
        }
        var year = deprecationDate.getFullYear();
        if (deprecationDate.getMonth() >= 10) {
            year++;
        }
        return "" + year;
    }
    const addDependencies = function (dep: EditableAccountDependency | EditableAccountSubDependency, childRows: DependencyTreeItemA[] ) {
        if (dep.dependencies != null && dep.dependencies.length > 0) {
            var nodeName = "Dependencies";
            switch (dep.relationship) {
                case enums.dependencyRelationship.NOT_EXISTS:
                    nodeName = "None of these can exist";
                    break;

                case enums.dependencyRelationship.EXISTS:
                    nodeName = "All of these must exist";
                    break;

                case enums.dependencyRelationship.EXISTS_ONE:
                    nodeName = "Only one of these can exist";
                    break;

                case enums.dependencyRelationship.EXISTS_ANY:
                    nodeName = "At least one of these must exist";
                    break;

            }
            var dItem: DependencyTreeItemA = {
                id: utilityHelper.generateGuid(),
                name: nodeName,
                type: "property",
                editing: false,
                changed: false,
                remove: false,
                children: []
            }
            childRows.push(dItem);
            dep.dependencies.map(sub => createTreeChildItem(dItem, sub))
        }
    }
    const createTreeChildItem = function (parent : DependencyTreeItemA, dep: EditableAccountSubDependency): DependencyTreeItemA {
        var newItem: DependencyTreeItemA = {
            id: dep.id,
            name: getSummaryName(dep),
            type: "sub",
            editing: dep.editable,
            changed: dep.changed,
            remove: dep.remove,
            children: null
        }
        var childRows: DependencyTreeItemA[] = [];
        addDependencies(dep, childRows);
        if (childRows.length > 0) {
            newItem.children = childRows;
        }
        if (parent.children) {
            parent.children.push(newItem);
        } else {
            parent.children = [newItem];
        }
        return newItem;
    }
    const createTreeItem = function (dep: EditableAccountDependency): DependencyTreeItemA {
        var conceptIds: string[] = [];
        if (dep.target) {
            conceptIds.push(dep.target.code);
        }
        var newItem: DependencyTreeItemA = {
            id: dep.id,
            name: getSummaryName(dep),
            type: "root",
            editing: dep.editable,
            changed: dep.changed,
            remove: dep.remove,
            children: null
        }
        if (dep.remove) {
            return newItem;
        }
        var childRows: DependencyTreeItemA[] = [];
        if (dep.preconditions != null && dep.preconditions.length > 0) {
            var pItem: DependencyTreeItemA = {
                id: utilityHelper.generateGuid(),
                name: "Preconditions",
                type: "property",
                editing: false,
                changed: false,
                remove: false,
                children: []
            }
            childRows.push(pItem);
            dep.preconditions.map(sub => createTreeChildItem(pItem, sub));
        }
        addDependencies(dep, childRows);
        if (childRows.length > 0) {
            newItem.children = childRows;
        }
        return newItem;
    }
    const addRuleToTree = function(d: EditableAccountDependency, result: DependencyTreeItemA[]) {
        if (d.remove && d.isNew) {
            return;
        }
        result.push(createTreeItem(d));
    }
    const generateDependencyTreeData = function (dset: EditableAccountDependency[]): DependencyTreeItemA[] {
        var result: DependencyTreeItemA[] = [];
        if (dset.length > 0) {
            dset.map(d => addRuleToTree(d, result));
        }
        return result;
    }
    const updateDepRemoveText = function() {

    }
    const [treeData, setTreeData] = useState<DependencyTreeItem>({ name: "", metadata: null, children: []});
    const treeDepOnSelect = function (props: { element: any, isBranch: boolean, isExpanded: boolean, isSelected: boolean, isHalfSelected: boolean, isDisabled: boolean, treeState: any }) {

        if (!props.element.metadata) {
            return;
        }
        if (!props.isSelected) {
            return;
        }
        var dep: any = props.element.metadata;
        var axesFilter: NetworkConceptsFilter = {
            taxonomyVersion: getReferenceForVersion(versionInfo, -1),
            nameFilter: dep.target ? dep.target.conceptId : "none"
        }
        dispatch(loadAxesForConcept(axesFilter)).then((result: any) => {
            setSelectedDependency(dep);
            if (isSubDependency(dep)) {                
                setSelectedDependencyIsRoot(false);
                setCanMakeRootDependencyEditable(false);
                populateSubDependencyStates(dep as EditableAccountSubDependency);
            } else {
                setSelectedDependencyIsRoot(true);
                setCanMakeRootDependencyEditable(!dep.editable && !dep.remove);
                populateRootDependencyStates(dep as EditableAccountDependency);
            }
            
        });

    }
    const findChildDependency = function (child: EditableAccountSubDependency, id: string): EditableAccountDependency | EditableAccountSubDependency | null {
        var result: EditableAccountDependency | EditableAccountSubDependency | null = null;
        if (child.id === id) {
            return child;
        }
        if (child.dependencies) {
            for (var sub of child.dependencies) {
                result = findChildDependency(sub, id);
                if (result) {
                    return result;
                }
            }
        }
        return result;
    }
    const findRootDependency = function (root: EditableAccountDependency, id: string): EditableAccountDependency | EditableAccountSubDependency | null {
        var result: EditableAccountDependency | EditableAccountSubDependency | null = null;
        if (root.id === id ) {
            return root;
        }
        if (root.preconditions) {
            for (var child of root.preconditions) {
                result = findChildDependency(child, id);
                if (result) {
                    return result;
                }
            }
        }
        if (root.dependencies) {
            for (var child of root.dependencies) {
                result = findChildDependency(child, id);
                if (result) {
                    return result;
                }
            }
        }
        return result;
    }
    const findDependency = function(id: string) : EditableAccountDependency | EditableAccountSubDependency | null {
        var result: EditableAccountDependency | EditableAccountSubDependency | null = null;
        if (dependencySet && dependencySet.length > 0) {
            for (var root of dependencySet) {
                result = findRootDependency(root, id);
                if (result) {
                    return result;
                }
            }
        }
        return result;
    }
    const treeDepOnSelectA = function (nodes: NodeApi<DependencyTreeItemA>[]) {
        if (nodes.length === 0) {
            return;
        }
        var n: NodeApi<DependencyTreeItemA> = nodes[0];
        var dep: EditableAccountDependency | EditableAccountSubDependency | null = findDependency(n.id);
        if (!dep) {
            return;
        }
        var axesFilter: NetworkConceptsFilter = {
            taxonomyVersion: getReferenceForVersion(versionInfo, -1),
            nameFilter: dep.target ? dep.target.conceptId : "none"
        }
        var rootEditable = false;
        dispatch(loadAxesForConcept(axesFilter)).then((result: any) => {
            setSelectedDependency(dep);
            if (!dep || isSubDependency(dep)) {
                setSelectedDependencyIsRoot(false);
                setCanMakeRootDependencyEditable(false);
                populateSubDependencyStates(dep as EditableAccountSubDependency);
            } else {
                setSelectedDependencyIsRoot(true);
                setCanMakeRootDependencyEditable(!dep.editable && !dep.remove);
                populateRootDependencyStates(dep as EditableAccountDependency);
            }

        });
    
    }
    const isAbstractNode = function (element: any) {
        if (element.metadata) {
            return false;
        }
        return true;
    }
    function sizedTree(width: number, height: number) {
        return (
            <Tree
                className="Tree"
                openByDefault={false}
                data={treeModel}
                width={width}
                height={height}
                paddingTop={20}
                indent={20}
                onSelect={treeDepOnSelectA}
            >{TreeNode}</Tree>
        );
    }
    function SingleSelectCheckbox() {
        return (
            <div style={{ width: "100%" }}>
                {treeModel.length > 0 &&
                    <div style={{ width: "100%", height: "40vh" }} >
                        <Autosize>
                            {(props : any) => (
                                <Tree
                                    className="Tree"
                                    data={treeModel}
                                    width={props.width}
                                    height={props.height}
                                    indent={20}
                                    onSelect={treeDepOnSelectA}
                                >{TreeNode}</Tree>
                            )}
                        </Autosize>
                    </div>
                }
            </div>
        );
    }
    function SingleSelectPriorYearsCheckbox() {
        return (
            <div style={{ width: "100%" }}>
                {priorTreeModel.length > 0 &&
                    <div style={{ width: "100%", height: "40vh" }} >
                        <Autosize>
                            {(props: any) => (
                                <Tree
                                    className="Tree"
                                    data={priorTreeModel}
                                    width={props.width}
                                    height={props.height}
                                    indent={20}
                                    onSelect={treeDepOnSelectA}
                                >{TreeNode}</Tree>
                            )}
                        </Autosize>
                    </div>
                }
            </div>
        );
    }
    function SingleSelectCheckboxY() {
        return (
            <div>
                {treeModel.length > 0 && 
                                sizedTree(700, 1000)
                }
            </div>
        );
    }
    function AllRelationships() {
        return (
            <FormGroup>
                <Row style={leftMarginStyle}>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.NONE}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.NONE)}
                                /><b>None</b>
                            </Label>
                        </Row>
                        {selectedDependency && selectedDependency.isRule &&
                            <Row>
                                <Label className="ml-4" check>
                                    <Input type="radio"
                                        name="depRel"
                                        value={enums.dependencyRelationship.TEST}
                                        onChange={handleRelChange}
                                        checked={(depRelationship === enums.dependencyRelationship.TEST)}
                                    /><b>Test</b>
                                </Label>
                            </Row>
                        }
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS)}
                                /><b>Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS_ANY}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS_ANY)}
                                /><b>Any Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS_ONE}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS_ONE)}
                                /><b>One Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.NOT_EXISTS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.NOT_EXISTS)}
                                /><b>Not Exists</b>
                            </Label>
                        </Row>
                    </Col>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EQUALS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EQUALS)}
                                /><b>=</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.GREATER_THAN}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.GREATER_THAN)}
                                /><b>&gt;</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.LESS_THAN}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.LESS_THAN)}
                                /><b>&lt;</b>
                            </Label>
                        </Row>
                    </Col>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.GREATER_THAN_EQUAL}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.GREATER_THAN_EQUAL)}
                                /><b>&gt;=</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.LESS_THAN_EQUAL}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.LESS_THAN_EQUAL)}
                                /><b>&lt;=</b>
                            </Label>
                        </Row>
                    </Col>
                </Row>
            </FormGroup>
        );
    }
    function DependentRelationships() {
        return (
            <FormGroup>
                <Row style={leftMarginStyle}>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.NONE}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.NONE)}
                                /><b>None</b>
                            </Label>
                        </Row>
                        {selectedDependency && selectedDependency.isRule &&

                            <Row>
                                <Label className="ml-4" check>
                                    <Input type="radio"
                                        name="depRel"
                                        value={enums.dependencyRelationship.TEST}
                                        onChange={handleRelChange}
                                        checked={(depRelationship === enums.dependencyRelationship.TEST)}
                                    /><b>Test</b>
                                </Label>
                            </Row>
                        }
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS)}
                                /><b>Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS_ANY}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS_ANY)}
                                /><b>Any Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS_ONE}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS_ONE)}
                                /><b>One Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.NOT_EXISTS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.NOT_EXISTS)}
                                /><b>Not Exists</b>
                            </Label>
                        </Row>
                    </Col>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EQUALS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EQUALS)}
                                /><b>=</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.GREATER_THAN}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.GREATER_THAN)}
                                /><b>&gt;</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.LESS_THAN}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.LESS_THAN)}
                                /><b>&lt;</b>
                            </Label>
                        </Row>
                    </Col>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.GREATER_THAN_EQUAL}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.GREATER_THAN_EQUAL)}
                                /><b>&gt;=</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.LESS_THAN_EQUAL}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.LESS_THAN_EQUAL)}
                                /><b>&lt;=</b>
                            </Label>
                        </Row>
                    </Col>
                </Row>
            </FormGroup>
        );
    }
    function NumericRelationships() {
        return (
            <FormGroup>
                <Row style={leftMarginStyle}>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.NONE}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.NONE)}
                                /><b>None</b>
                            </Label>
                        </Row>
                        {selectedDependency && selectedDependency.isRule &&
                            <Row>
                                <Label className="ml-4" check>
                                    <Input type="radio"
                                        name="depRel"
                                        value={enums.dependencyRelationship.TEST}
                                        onChange={handleRelChange}
                                        checked={(depRelationship === enums.dependencyRelationship.TEST)}
                                    /><b>Test</b>
                                </Label>
                            </Row>
                        }
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS)}
                                /><b>Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS_ANY}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS_ANY)}
                                /><b>Any Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS_ONE}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS_ONE)}
                                /><b>One Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.NOT_EXISTS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.NOT_EXISTS)}
                                /><b>Not Exists</b>
                            </Label>
                        </Row>
                    </Col>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EQUALS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EQUALS)}
                                /><b>=</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.GREATER_THAN}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.GREATER_THAN)}
                                /><b>&gt;</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.LESS_THAN}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.LESS_THAN)}
                                /><b>&lt;</b>
                            </Label>
                        </Row>
                    </Col>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.GREATER_THAN_EQUAL}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.GREATER_THAN_EQUAL)}
                                /><b>&gt;=</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.LESS_THAN_EQUAL}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.LESS_THAN_EQUAL)}
                                /><b>&lt;=</b>
                            </Label>
                        </Row>
                    </Col>
                </Row>
            </FormGroup>
        );
    }
    function BooleanRelationships() {
        return (
            <FormGroup>
                <Row style={leftMarginStyle}>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.NONE}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.NONE)}
                                /><b>None</b>
                            </Label>
                        </Row>
                        {selectedDependency && selectedDependency.isRule &&
                            <Row>
                                <Label className="ml-4" check>
                                    <Input type="radio"
                                        name="depRel"
                                        value={enums.dependencyRelationship.TEST}
                                        onChange={handleRelChange}
                                        checked={(depRelationship === enums.dependencyRelationship.TEST)}
                                    /><b>Test</b>
                                </Label>
                            </Row>
                        }
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EXISTS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EXISTS)}
                                /><b>Exists</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.NOT_EXISTS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.NOT_EXISTS)}
                                /><b>Not Exists</b>
                            </Label>
                        </Row>
                    </Col>
                    <Col>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EQUALS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.EQUALS)}
                                /><b>is</b>
                            </Label>
                        </Row>
                        <Row>
                            <Label className="ml-4" check>
                                <Input type="radio"
                                    name="depRel"
                                    value={enums.dependencyRelationship.EQUALS}
                                    onChange={handleRelChange}
                                    checked={(depRelationship === enums.dependencyRelationship.NOT_EQUALS)}
                                /><b>is not</b>
                            </Label>
                        </Row>
                     </Col>
                </Row>
            </FormGroup>
        );
    }
    const TargetRelationships = function () {
        if (!selectedDependency || (selectedDependency.dependencies && selectedDependency.dependencies.length > 0)) {
            return AllRelationships();
        }
        if (targetIsBoolean()) {
            return BooleanRelationships();
        }
        if (targetIsNumeric()) {
            return NumericRelationships();
        }
        return AllRelationships();
    }
    const clearDependencySelection = function () {
        setDependentsList(getDependentsList(null));
        dispatch(clearAxesForConcepts);
        setSelectedDependency(null);
        clearDependencyStates();
    }
    const handleNewDependencyClick = () => {
        if (!selectedDependency) {
            return;
        }
        // TO FIX        dset.push({ id: utilityHelper.generateGuid(), name: utilityHelper.generateGuid(), accounts: [], relationship: "", value: null, test: null, changed: true, remove: false })
        var depName = utilityHelper.generateGuid();
        var newDependency: EditableAccountSubDependency = {
            editable: true,
            year: null,
            id: depName,
            name: null,
            target: null,
            dependencies: [],
            relationship: enums.dependencyRelationship.NONE,
            value: null,
            booleanValue: null,
            fallback: null,
            booleanFallback: null,
            isSequence: null,
            test: null,
            changed: true,
            remove: false,
            dimensionQualifiers: null,
            dimensionDisQualifiers: null,
            dimensionsToMatch: null,
            decimals: null,
            isRule: false,
            root: null
        }
        if (selectedDependency.dependencies) {
            selectedDependency.dependencies.unshift(newDependency);
        } else {
            selectedDependency.dependencies = [newDependency];
        }
        updateDependencySet(selectedDependency);
//        setTreeModel(generateDependencyTreeData(dset));
        clearDependencySelection();
    }
    const handleDependencyDateChange = function (dateEntered: any) {
        if (selectedDependency) {
            // set the dirty flag to TRUE
            if (!dateEntered || dateEntered.length === 0) {
                setDeprecationDate(null);
                return;
            }
            if (isNaN(Date.parse(dateEntered))) {
                return;
            }
            setDeprecationDate(dateEntered);
            selectedDependency.changed = true;
            var edit: EditableAccountDependency = selectedDependency as EditableAccountDependency;
            edit.deprecationDate = dateEntered;
            updateDependencySet(selectedDependency);
        }
    }
    const handleDeprecationYearChange = function (event: any, action: any) {
        if (selectedDependency) {
            var edit: EditableAccountDependency = selectedDependency as EditableAccountDependency;
            if (event.value === 0) {
                setDeprecationDate(null);
                edit.deprecationDate = null;

            } else {
                var dateEntered: Date = new Date(event.value - 1, 10, 1);
                setDeprecationDate(dateEntered);
                edit.deprecationDate = dateEntered;
            }
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const getDecimalsOptions = function () : any[] {
        var result: any[] = [
            { label: "infinite", value: null },
            { label: "2 decimals", value: 2 },
            { label: "1 decimal", value: 1 },
            { label: "no decimal", value: 0 },
            { label: "hundreds", value: 2 },
            { label: "thousands", value: 3 },
            { label: "millions", value: 6 },
        ];
        return result;
    }
    const handleDecimalsChange = function (event: any, action: any) {
        if (selectedDependency) {
            var decs = event.value;
            if (!decs) {
                setDecimals(null);
                selectedDependency.decimals = null;
            } else {
                setDecimals(decs);
                selectedDependency.decimals = decs;
            }
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }

    }

    const handleNewPreconditionClick = () => {
        if (!selectedDependency) {
            return;
        }
        // TO FIX        dset.push({ id: utilityHelper.generateGuid(), name: utilityHelper.generateGuid(), accounts: [], relationship: "", value: null, test: null, changed: true, remove: false })
        var depName = utilityHelper.generateGuid();
        var newDependency: EditableAccountSubDependency = {
            editable: true,
            year: null,
            id: depName,
            name: null,
            target: null,
            dependencies: [],
            relationship: enums.dependencyRelationship.NONE,
            value: null,
            booleanValue: null,
            fallback: null,
            booleanFallback: null,
            isSequence: null,
            test: null,
            changed: true,
            remove: false,
            dimensionQualifiers: null,
            dimensionDisQualifiers: null,
            dimensionsToMatch: null,
            decimals: null,
            isRule: false,
            root: null
        }
        var root = selectedDependency as EditableAccountDependency;
        if (root) {
            if (root.preconditions) {
                root.preconditions.unshift(newDependency);
            } else {
                root.preconditions = [newDependency];
            }
        }
        updateDependencySet(selectedDependency);
        //        setTreeModel(generateDependencyTreeData(dset));
        clearDependencySelection();
    }
    const handleNewRuleClick = () => {
        var dset = [...dependencySet];
        // TO FIX        dset.push({ id: utilityHelper.generateGuid(), name: utilityHelper.generateGuid(), accounts: [], relationship: "", value: null, test: null, changed: true, remove: false })
        var depName = utilityHelper.generateGuid();
        var t: ConceptDisplayRef | null = null;
        if (versionedConcept && versionedConcept.concept) {
            t = {
                afrYear: versionYear,
                conceptId: versionedConcept.concept.id,
                conceptName: versionedConcept.concept.name,
                code: versionedConcept.concept.code,
                datatype: versionedConcept.concept.datatype
            }
        }
        var newDependency: EditableAccountDependency = {
            ruleId: null,
            editable: true,
            year: versionYear,
            id: depName,
            name: depName,
            target: t,
            dependencies: null,
            preconditions: null,
            relationship: enums.dependencyRelationship.TEST,
            value: null,
            booleanValue: null,
            fallback: null,
            booleanFallback: null,
            isSequence: null,
            test: null,
            message: null,
            changed: true,
            remove: false,
            dimensionQualifiers: null,
            dimensionDisQualifiers: null,
            dimensionsToMatch: null,
            isRule: true,
            newName: null,
            isNew: true,
            deprecationDate: null,
            decimals: null
        }
        dset.unshift(newDependency);
        setDependencySet(dset);
        //        setTreeModel(generateDependencyTreeData(dset));
        clearDependencySelection();
    }
    const isSubDependency = function(dep: any) {
        return !dep.hasOwnProperty("message");
    }
    const hasExistenceRelationship = function (rel: string) {
        if (!rel) {
            return false;
        }
        return rel.indexOf("EXIST") >= 0;
    }
    const hasTestRelationship = function (rel: string) {
        if (!rel) {
            return false;
        }
        return rel.indexOf(enums.dependencyRelationship.TEST) >= 0;
    }
    const getDependencyConceptType = function (): string | null {
        if (selectedDependency && selectedDependency.target) {
            return selectedDependency.target.datatype;
        }
        if (versionedConcept && versionedConcept.concept) {
            return versionedConcept.concept.datatype;
        }
        return null;
    }
    const hasValueRelationship = function (rel: string, dtypes: string[]) {
        if (!rel || !selectedDependency ) {
            return false;
        }
        if ( !selectedDependency.isRule && ( !selectedDependency.target || !selectedDependency.target.datatype) ) {
            return false;
        }
        var targetType: string | null = getDependencyConceptType(); 
        if (!targetType) {
            return false;
        }
        if (rel.indexOf(enums.dependencyRelationship.TEST) >= 0) {
            return false;
        }
        if (rel.indexOf("EXIST") >= 0) {
            return false;
        }
        if (rel == enums.dependencyRelationship.NONE) {
            return false;
        }
        if (selectedDependency.dependencies && selectedDependency.dependencies.length > 0) {
            return false;
        }
        return dtypes.indexOf(targetType) >= 0;
    }
    const targetHasType = function (dtypes: string[]) {
        if (!selectedDependency) {
            return false;
        }
        var targetType: string | null = getDependencyConceptType();
        if (!targetType) {
            return false;
        }
        return dtypes.indexOf(targetType) >= 0;
    }
    const targetIsAbstract = function () : boolean {
        if (!selectedDependency) {
            return true;
        }
        if (selectedDependency && selectedDependency.target) {
            // Assume the target of a dependency is non-abstract
            console.log("dep target", selectedDependency.target);
            return false;
        }
        // This is the root concept
        if (versionedConcept && versionedConcept.concept && (versionedConcept.concept.isAbstract != null)) {
            console.log("vc", versionedConcept.concept.isAbstract);
            return versionedConcept.concept.isAbstract;
        }
        return true;
    }
    const hasMonetaryValueRelationship = function (rel: string) {
        var result = hasValueRelationship(rel, ["XBRLI_MONETARYITEMTYPE"]);
        return result;
    }
    const hasBooleanValueRelationship = function (rel: string) {
        return hasValueRelationship(rel, ["XBRLI_BOOLEANITEMTYPE","UAS_CODES_YES_NO_ITEM_TYPE"]);
    }
    const hasPercentValueRelationship = function (rel: string) {
        return hasValueRelationship(rel, ["DTR_TYPES_PERCENTITEMTYPE", "DTR_TYPES_PERCENT_ITEM_TYPE"]);
    }
    const hasIntegerValueRelationship = function (rel: string) {
        return hasValueRelationship(rel, ["XBRLI_NONNEGATIVEINTEGERITEMTYPE"]);
    }
    const targetIsNumeric = function () {
        return targetHasType(["XBRLI_MONETARYITEMTYPE", "DTR_TYPES_PERCENT_ITEM_TYPE", "XBRLI_NONNEGATIVEINTEGERITEMTYPE"]);
    }
    const targetIsMonetary = function () {
        return targetHasType(["XBRLI_MONETARYITEMTYPE"]);
    }
    const targetIsBoolean = function () {
        return targetHasType( ["XBRLI_BOOLEANITEMTYPE", "UAS_CODES_YES_NO_ITEM_TYPE"]);
    }
    const targetIsPercent = function () {
        return targetHasType(["DTR_TYPES_PERCENTITEMTYPE"]);
    }
    const targetIsInteger = function () {
        return targetHasType( ["XBRLI_NONNEGATIVEINTEGERITEMTYPE"]);
    }
    const handleRemoveDependencyClick = () => {
        if (!selectedDependency) {
            return;
        }
        setCanMakeRootDependencyEditable(false);
        setTreeModel(generateDependencyTreeData(dependencySet));
        clearDependencySelection();
    }
    const removeSubDependency = function (idToRemove: string, parent: EditableAccountSubDependency) {
        var result: EditableAccountSubDependency[] = [];
        for (var sub of parent.dependencies) {
            if (sub.id === idToRemove) {
                parent.changed = true;
                continue;
            }
            result.push(sub);
            if (sub.dependencies.length > 0 ) {
                removeSubDependency(idToRemove, sub);
            }
        }
        parent.dependencies = result;
    }
    const removePrecondition = function (idToRemove: string, parent: EditableAccountDependency) {
        if (!parent.preconditions || parent.preconditions.length === 0) {
            return;
        }
        var result: EditableAccountSubDependency[] = [];
        for (var sub of parent.preconditions) {
            if (sub.id === idToRemove) {
                parent.changed = true;
                continue;
            }
            if (sub.dependencies.length > 0) {
                removeSubDependency(idToRemove, sub);
            }
            result.push(sub);
        }
        if (result.length === 0) {
            parent.preconditions = null;
        } else {
            parent.preconditions = result;
        }
    }
    const handleTreeRemoveDependency = (nodes: NodeApi<DependencyTreeItemA>[] ) => {
        if (!selectedDependency || dependencySet.length === 0) {
            return;
        }
        if (nodes.length === 0) {
            return;
        }
        var workingSet: EditableAccountDependency[] = [...dependencySet];
        var newSet: EditableAccountDependency[] = [];
        for (var n of nodes) {
            var id = n.data.id;
            for (var rootDep of workingSet) {
                if (rootDep.id === id) {
                    rootDep.remove = true;
                } else {
                    removePrecondition(id, rootDep);
                    var newDeps: EditableAccountSubDependency[] = [];
                    if (rootDep.dependencies) {
                        for (var sub of rootDep.dependencies) {
                            if (sub.id === id) {
                                rootDep.changed = true;
                                continue;
                            }
                            newDeps.push(sub);
                            if (sub.dependencies.length > 0) {
                                removeSubDependency(id, sub);
                            }
                        }
                    }
                    rootDep.dependencies = newDeps;
                }
                newSet.push(rootDep);
            }
            workingSet = newSet;
            newSet = [];

        }
        setDependencySet(workingSet);
        clearDependencySelection();
    }
    const handleTreeRemoveDependencyClick = () => {
        if (!selectedDependency || dependencySet.length === 0) {
            return;
        }
        if (selectedDependency.isRule) {
            selectedDependency.remove = true;
            selectedDependency.changed = true;
            selectedDependency.editable = false;
            updateDependencySet(selectedDependency);
            return;
        }
        var workingSet: EditableAccountDependency[] = [...dependencySet];
        var newSet: EditableAccountDependency[] = [];
            var id = selectedDependency.id;
            for (var rootDep of workingSet) {
                if (rootDep.id === id) {
                    rootDep.remove = true;
                } else {
                    removePrecondition(id, rootDep);
                    var newDeps: EditableAccountSubDependency[] = [];
                    if (rootDep.dependencies) {
                        for (var sub of rootDep.dependencies) {
                            if (sub.id === id) {
                                rootDep.changed = true;
                                continue;
                            }
                            newDeps.push(sub);
                            if (sub.dependencies.length > 0) {
                                removeSubDependency(id, sub);
                            }
                        }
                    }
                   rootDep.dependencies = newDeps;
                }
                newSet.push(rootDep);
            }
            workingSet = newSet;
            newSet = [];

        setDependencySet(workingSet);
        clearDependencySelection();
    }
    const handleMakeEditableDependencyClick = () => {
        if (!selectedDependency) {
            return;
        }
        if (!versionInfo) {
            return;
        }
        var taxYear = versionInfo.year;
        makeEditable(selectedDependency as EditableAccountDependency, taxYear);
        updateDependencySet(selectedDependency);
        toggle("current");
    }
    const createSubDependencyInputList = function (depList: EditableAccountSubDependency[] | null): AccountDependencyInput[] {
        var result: AccountDependencyInput[] = [];
        if (depList) {
            result = depList.map(dep => createAccountSubDependencyInput(dep));
        }
        return result;
    }
    const createDimensionReferenceInputList = function (dimList: DimensionReference[] | null): DimensionReferenceInput[] {
        var result: DimensionReferenceInput[] = [];
        if (dimList) {
            dimList.map(dr => {
                var dri: DimensionReferenceInput = {
                    axisId: dr.axis.id,
                    memberId: dr.member ? dr.member.id : "",
                    typedContent: dr.typedContent
                };
                result.push(dri);
            });
        }
        return result;
    }
    const createAccountSubDependencyInput = function (dep: EditableAccountSubDependency): AccountDependencyInput {
        var result: AccountDependencyInput = {
            name: dep.name,
            dependency: [],
            recursiveDependencies: createSubDependencyInputList(dep.dependencies),
            relationship: dep.relationship,
            value: dep.value,
            booleanValue: dep.booleanValue,
            fallback: dep.fallback,
            booleanFallback: dep.booleanFallback,
            isSequence: dep.isSequence,
            test: dep.test,
            dimensionQualifiers: createDimensionReferenceInputList(dep.dimensionQualifiers),
            dimensionDisQualifiers: createDimensionReferenceInputList(dep.dimensionDisQualifiers),
            dimensionsToMatch: createDimensionReferenceInputList(dep.dimensionsToMatch),
            decimals: dep.decimals,
            conceptId: dep.target ? dep.target.conceptId : ""
        }
        return result;
    }
    const createAccountDependencyInput = function (dep: EditableAccountDependency): AccountRootDependencyInput {
        var result: AccountRootDependencyInput = {
            ruleId: dep.ruleId,
            name: dep.name,
            dependency: [],
            recursiveDependencies: createSubDependencyInputList(dep.dependencies),
            preconditions: createSubDependencyInputList(dep.preconditions),
            relationship: dep.relationship,
            value: dep.value,
            booleanValue: dep.booleanValue,
            fallback: dep.fallback,
            booleanFallback: dep.booleanFallback,
            isSequence: dep.isSequence,
            test: dep.test,
            dimensionQualifiers: createDimensionReferenceInputList(dep.dimensionQualifiers),
            dimensionDisQualifiers: createDimensionReferenceInputList(dep.dimensionDisQualifiers),
            dimensionsToMatch: createDimensionReferenceInputList(dep.dimensionsToMatch),
            conceptId: dep.target ? dep.target.conceptId : "",
            newName: dep.newName,
            remove: dep.remove,
            message: dep.message,
            deprecationDate: dep.deprecationDate ? dep.deprecationDate.toISOString() : null,
            decimals: dep.decimals
        }
        return result;
    }

    const clearValueEntries = function (dep: EditableAccountDependency | EditableAccountSubDependency | null) {
        if (dep) {
            dep.value = null;
            dep.fallback = null;
            dep.booleanFallback = null;
            dep.booleanValue = null;
            dep.changed = true;
        }
        setDepValue("");
        setDepBooleanValue(null);
        setDepFallback("");
        setDepBooleanFallback(null);
    }
    const handleSaveDependenciesClick = function () {
        if (dependencySet.length === 0) {
            return;
        }
        if (!versionedConcept || !versionedConcept.concept) {
            return;
        }
        var depList: AccountRootDependencyInput[] = [];
        dependencySet.map(ead => hasChange(ead) ? depList.push(createAccountDependencyInput(ead)) : "" );
        var saveSet: AccountDependencySetInput = {
            accountId: versionedConcept.concept.id,
            dependencies: depList
        }
        dispatch(addAccountDependencyChange(saveSet));
        dependencySet.map(ead => ead.changed = false);
        setDepChanges(false);
    }
    const handleRestoreDependenciesClick = function () {
        dispatch(clearChanges());
        initializeDependencySet(versionedConcept);
    }
    const handleRelChange = function (event: any) {
        if (selectedDependency) {
            var newRel: string = event.target.value;
            selectedDependency.relationship = newRel;
            selectedDependency.test = null;
            setDepTest("");
            if (newRel.indexOf(enums.dependencyRelationship.EXISTS) >= 0) {
                selectedDependency.value = null;
                setDepValue("");
                setDepBooleanValue(null);
                setDepFallback("");
                setDepBooleanValue(null);
                setDepBooleanValue(null);
            } else {
                switch (newRel) {
                    case enums.dependencyRelationship.TEST:
                    case enums.dependencyRelationship.NONE:
                        selectedDependency.value = null;
                        setDepValue("");
                        setDepBooleanValue(null);
                        break;
                }
            }
            selectedDependency.changed = true;
            setDepRelationship(newRel);
            updateDependencySet(selectedDependency);
        }
    }
    const handleValueChange = function (newValue: string) {
        if (selectedDependency) {
            var val: number = parseFloat(newValue);
            if (isNaN(val)) {
                val = 0;
            }
            selectedDependency.value = val;
            setDepValue(val + "");
            selectedDependency.test = null;
            setDepTest("");
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const handlePercentValueChange = function (val: number | undefined) {
        if (selectedDependency) {
            if (val === undefined) {
                selectedDependency.value = null;
                setDepValue("");
            } else {
                var roundedVal: number = Math.round(val * 10000) / 1000000;
                selectedDependency.value = roundedVal;
                setDepValue(roundedVal.toString());
            }
            selectedDependency.test = null;
            setDepTest("");
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const handleBooleanValueChange = function (val: boolean) {
        if (selectedDependency) {
            selectedDependency.booleanValue = val;
            setDepBooleanValue(val);
            selectedDependency.test = null;
            setDepTest("");
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const handleBooleanValueChange_radio = function (e: any) {
        handleBooleanValueChange(convertTextToBoolean(e.target.value));
    }
    const handleFallbackChange = function (newValue: string) {
        if (selectedDependency) {
            var val: number = parseFloat(newValue);
            if (isNaN(val)) {
                val = 0;
            }
            selectedDependency.fallback = val;
            setDepFallback(val + "");
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
       }
    }
    const handleBooleanFallbackChange = function (val: boolean) {
        if (selectedDependency) {
            selectedDependency.booleanFallback = val;
            setDepBooleanFallback(val);
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const handleBooleanFallbackChange_radio = function (e: any) {
        handleBooleanFallbackChange(convertTextToBoolean(e.target.value));
    }
    const handleIsSequenceChange = function (newValue: string) {
        if (selectedDependency) {
            var val: boolean | null = null;
            if (newValue.length > 0) {
                switch (newValue.toLocaleLowerCase()) {
                    case "true":
                    case "yes":
                    case "t":
                    case "y":
                        val = true;
                        break;

                    case "false":
                    case "no":
                    case "f":
                    case "n":
                        val = false;
                        break;
                }
            }
            selectedDependency.isSequence = val;
            setDepIsSequence(val + "");
            selectedDependency.changed = true;
        }
    }
    const handleIsSequenceChangeBoolean = function (val: boolean) {
        if (selectedDependency) {
            selectedDependency.isSequence = val;
            setDepIsSequence(val + "");
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const handleTestChange = function (newValue: string) {
        if (selectedDependency) {
            clearValueEntries(selectedDependency);
            selectedDependency.test = newValue;
            setDepTest(newValue);
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const handleMessageChange = function (newValue: string) {
        if (selectedDependency && selectedDependency.isRule) {
            var sel = selectedDependency as EditableAccountDependency;
            sel.message = newValue;
            setDepMessage(newValue);
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const handleNameChange = function (newValue: string) {
        if (selectedDependency) {
            setDepName(newValue);
            if (selectedDependency.isRule) {
                var rule: EditableAccountDependency = selectedDependency as EditableAccountDependency;
                if (rule.isNew) {
                    rule.name = newValue;
                } else {
                    rule.newName = newValue;
                }
            } else {
                selectedDependency.name = newValue;
            }
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const handleRuleIdChange = function (newValue: string) {
        if (selectedDependency) {
            setDepRuleId(newValue);
            if (selectedDependency.isRule) {
                var rule: EditableAccountDependency = selectedDependency as EditableAccountDependency;
                if (rule) {
                    rule.ruleId = newValue;
                }
            }
            selectedDependency.changed = true;
            updateDependencySet(selectedDependency);
        }
    }
    const hasSubChange = function (dep: EditableAccountSubDependency): boolean {
        if (dep.changed) {
            return true;
        }
        if (dep.dependencies) {
            for (var sub of dep.dependencies) {
                if (hasSubChange(sub)) {
                    return true;
                }
            }
        }
        return false;
    }
    const hasChange = function (dep: EditableAccountDependency): boolean {
        if (dep.changed) {
            return true;
        }

        if (dep.preconditions) {
            for (var sub of dep.preconditions) {
                if (hasSubChange(sub)) {
                    return true;
                }
            }
        }
        if (dep.dependencies) {
            for (var sub of dep.dependencies) {
                if (hasSubChange(sub)) {
                    return true;
                }
            }
        }
        return false;
    }
    const noChangesInDependencies = function (): boolean {
        if (!dependencySet || dependencySet.length === 0) {
            return true;
        }
        var changes: EditableAccountDependency | undefined = dependencySet.find(dep => hasChange(dep));
        return (changes == null);
    }


    useEffect(() => {
        setTaxonomyIsDirty(anyChanges(changes));
        setSelectedDependency(null);
    }, [changes]);

    useEffect(() => {
        dispatch(clearSelectedReference());
        setUnitsList(getUnitsList(unitHierarchy, versionedConcept));
        setDimensionsList(getDimensionsList(versionedConcept));
        clearDependencyStates();
        initializeDependencySet(versionedConcept);
        setSelectedDependency(null);
    }, [versionedConcept]);

    useEffect(() => {
        const currentSet: EditableAccountDependency[] = [];
        const priorSet: EditableAccountDependency[] = [];

        if (versionInfo) {
            var vyear = versionInfo.year;
            dependencySet.map(dep => dep.year === vyear ? currentSet.push(dep) : priorSet.push(dep));
        }
        var dti: DependencyTreeItemA[] = generateDependencyTreeData(currentSet);
        setTreeModel(dti);
        var dtiPrior: DependencyTreeItemA[] = generateDependencyTreeData(priorSet);
        setPriorTreeModel(dtiPrior);
        if (dependencySet.length === 0) {
            setSelectedDependency(null);
            setSelectedRuleIsDeprecated(false);
            clearDependencyStates();
        }
        var changes: EditableAccountDependency | undefined = dependencySet.find(dep => hasChange(dep));
        if (changes === undefined) {
            setDepChanges(false);
        } else {
            setDepChanges(true);
        }
    }, [dependencySet]);


    useEffect(() => {
        var crefList = changes ? changes.referenceChanges : [];
    }, [changes]);

    useEffect(() => {
        var crefList = changes ? changes.referenceChanges : [];
        setUnitsList(getUnitsList(unitHierarchy, versionedConcept));
    }, [unitHierarchy]);


    useEffect(() => {
        var rootDep: EditableAccountDependency = selectedDependency as EditableAccountDependency;
        if (rootDep) {
            setDimensionMatchList(getDimensionMatchList(rootDep));
        } else {
            var subDep = selectedDependency as EditableAccountSubDependency;
            if (subDep) {
                setDimensionMatchList(getDimensionMatchListForSub(subDep));
            } else {
                setDimensionMatchList([]);
            }
        }
        setDimensionQualifiersList(getDimensionQualifiersList(selectedDependency));
        setDimensionDisQualifiersList(getDimensionDisQualifiersList(selectedDependency));
    }, [axesForConcept]);

    useEffect(() => {
        if (selectedDependency && !selectedDependency.remove === true) {
            setDependentsList(getDependentsList(selectedDependency));
            setDimensionQualifiersList(getDimensionQualifiersList(selectedDependency));
            setDimensionDisQualifiersList(getDimensionDisQualifiersList(selectedDependency));
            if (selectedDependency.isRule) {
                setDimensionMatchList(getDimensionMatchList(selectedDependency as EditableAccountDependency));
            } else {
                setDimensionMatchList(getDimensionMatchListForSub(selectedDependency as EditableAccountSubDependency));
            }
        } else {
            setDependentsList(getDependentsList(null));
            setDimensionMatchList([]);
            setDimensionQualifiersList([]);
            setDimensionDisQualifiersList([]);
            setDepRelationship("");
            setDepValue("");
            setDepName("");
            setDepTest("");
        }
    }, [selectedDependency]);

    const secondElementStyle = {
        marginTop: '4pt',
        marginLeft: '1em'
    }
    const editButtonStyle = {
        marginTop: '4pt',
        marginLeft: '1em',
        marginBottom: '4pt'
    }
    const leftMarginStyle = {
        marginLeft: '1em'
    }
    const leftCBStyle = {
        marginLeft: '2em'
    }
    const selRefStyle = {
        marginTop: '8pt',
        marginLeft: '1em'
    }
    const rightAlignStyle = {
        align: 'right'
    }
    const flexRow = {
        display: 'flex'
    }
    const flexRightChild = {
        marginLeft: 'auto',
        marginRight: '1em'
    }
    const unitScopeSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                id="unitPicker"
                data={unitsList}
                keepTreeOnSearch={true} keepChildrenOnSearch={true}
                showPartiallySelected={true}
                onChange={ddhandleUnitChange}
                texts={{ placeholder: "Choose Unit..." }} />
        )
    }, [unitHierarchy, versionedConcept, props]);

    const breakdownScopeSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                id="dimensionPicker"
                data={dimensionsList}
                keepTreeOnSearch={true} keepChildrenOnSearch={true}
                showPartiallySelected={true}
                onChange={ddhandleDimensionChange}
                texts={{ placeholder: "Choose Dimension..." }} />
        )
    }, [unitHierarchy, versionedConcept, props]);

    const dependentAccountSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                id="dependentAccountPicker"
                mode="radioSelect"
                data={dependentsList}
                keepTreeOnSearch={true} keepChildrenOnSearch={true}
                showPartiallySelected={true}
                onChange={ddhandleDependentChange}
                texts={{ placeholder: "" }} />
        )
    }, [selectedDependency, dependentsList, props]);

    const dimensionsToMatchSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                id="dependentAccountPicker"
                data={dimensionMatchList}
                keepTreeOnSearch={true} keepChildrenOnSearch={true}
                showPartiallySelected={true}
                onChange={ddhandleMatchChange}
                texts={{ placeholder: "" }} />
        )
    }, [dimensionMatchList]);

    const dimensionQualifiersSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                id="dimensionaQualifiersPicker"
                data={dimensionQualifiersList}
                keepTreeOnSearch={true} keepChildrenOnSearch={true}
                showPartiallySelected={true}
                onChange={ddhandleQualifiersChange}
                texts={{ placeholder: "" }} />
        )
    }, [selectedDependency,dimensionQualifiersList]);

    const dimensionDisQualifiersSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                id="dimensionaQualifiersPicker"
                data={dimensionDisQualifiersList}
                keepTreeOnSearch={true} keepChildrenOnSearch={true}
                showPartiallySelected={true}
                onChange={ddhandleDisQualifiersChange}
                texts={{ placeholder: "" }} />
        )
    }, [selectedDependency,dimensionDisQualifiersList]);

    return (
        <React.Fragment>
            <Row>
                <Col>
                    <Row>
                        <div >
                            <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!versionedConcept} onClick={handleNewRuleClick}>Add Rule</Button>
                            <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!depChanges} onClick={handleSaveDependenciesClick}>Save</Button>
                            <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!depChanges && !taxonomyIsDirty} onClick={handleRestoreDependenciesClick}>Undo</Button>
                        </div>
                    </Row>
                    <Nav tabs>
                        <NavItem>
                            <NavLink
                                className={activeClass('current')}
                                onClick={() => { toggle('current'); }}
                            >
                                Rules for {versionYear}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeClass('prior')}
                                onClick={() => { toggle('prior'); }}
                            >
                                Rules for prior years
                            </NavLink>
                        </NavItem>
                    </Nav>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="current">
                            <Row>
                                {SingleSelectCheckbox()}
                            </Row>
                        </TabPane>
                        <TabPane tabId="prior">
                            <Row>
                                {SingleSelectPriorYearsCheckbox()}
                            </Row>
                        </TabPane>
                    </TabContent>
                </Col>
                <Col>
                    {selectedDependency
                        ?
                        <div>
                            {selectedDependency.editable
                                ?
                                <Row>
                                    {selectedDependency.isRule &&
                                        <Button type="button" style={leftMarginStyle} className="taxedit" onClick={handleNewPreconditionClick}>Add Precondition</Button>
                                    }
                                    <Button type="button" style={leftMarginStyle} className="taxedit" onClick={handleNewDependencyClick}>Add Dependency</Button>
                                    {selectedDependency.isRule 
                                        ?
                                        <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!selectedRuleIsRemovable()} onClick={handleTreeRemoveDependencyClick}>Remove Rule</Button>
                                        :
                                        <Button type="button" style={leftMarginStyle} className="taxedit" onClick={handleTreeRemoveDependencyClick}>{depRemoveText}</Button>
                                    }
                               </Row>
                                :
                                <Row>
                                    {selectedDependency.isRule && !selectedDependency.remove &&
                                        <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!canMakeRootDependencyEditable} onClick={handleMakeEditableDependencyClick}>{ruleEditText}</Button>
                                    }
                                    {selectedRuleIsRemovable() &&
                                        <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!selectedRuleIsRemovable()} onClick={handleTreeRemoveDependencyClick}>Remove Rule</Button>
                                    }
                                </Row>
                            }
                                        {selectedDependencyIsRoot
                                            ?
                                            <Row>
                                                <Col md="2">
                                                    <Label>Year</Label>
                                                </Col>
                                                <Col>
                                                    <h5>
                                                        {getDependencyYear(selectedDependency)}
                                                    </h5>
                                                </Col>
                                            </Row>
                                            :
                                            <div></div>
                                        }
                                        {selectedDependencyIsRoot
                                            ?
                                            <Row>
                                                <Col md="2">
                                                    <Label>Rule ID</Label>
                                                </Col>
                                                <Col>
                                                    <Input type="text" value={depRuleId}
                                                        onChange={(e) => handleRuleIdChange(e.currentTarget.value)}
                                                        disabled={!selectedDependency.editable} />
                                                </Col>
                                            </Row>
                                            :
                                            <div></div>
                                        }
                                        <Row>
                                            <Col md="2">
                                                <Label>Summary</Label>
                                            </Col>
                                            <Col>
                                                <h5 style={{fontStyle:"italic"}}>
                                                    {getSummary(selectedDependency)}
                                                </h5>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="2">
                                                <Label>Name:</Label>
                                            </Col>
                                            <Col>
                                                <Input type="text" value={depName}
                                                    onChange={(e) => handleNameChange(e.currentTarget.value)}
                                                    disabled={!selectedDependency.editable} />
                                            </Col>
                                        </Row>
                                        {selectedDependencyIsRoot
                                            ?
                                            <Row>
                                                <Col md="2">
                                                    <Label>Message:</Label>
                                                </Col>
                                                <Col>
                                                    <Input type="textarea" value={depMessage}
                                                        onChange={(e) => handleMessageChange(e.currentTarget.value)}
                                                        disabled={!selectedDependency.editable} />
                                                </Col>
                                            </Row>
                                            :
                                            <div></div>
                                        }
                                        <Row>
                                            <Col md="2">
                                                <Label>Account</Label>
                                            </Col>
                                            <Col>
                                                {selectedDependencyIsRoot
                                                    ?
                                                    <h5>Root</h5>
                                                    :
                                                    <h5>
                                                        {dependentAccountSelector}
                                                    </h5>
                                                }
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="2">
                                                <Label>Qualifiers</Label>
                                            </Col>
                                            <Col>
                                                {selectedDependency.editable
                                                    ?
                                                    <h5>
                                                        {dimensionQualifiersSelector}
                                                    </h5>
                                                    :
                                                    <h5>
                                                        {getQualifiersText(selectedDependency)}
                                                    </h5>
                                                }
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="2">
                                                <Label>Dis-Qualifiers</Label>
                                            </Col>
                                            <Col>
                                                {selectedDependency.editable
                                                    ?
                                                    <h5>
                                                        {dimensionDisQualifiersSelector}
                                                    </h5>
                                                    :
                                                    <h5>
                                                        {getDisQualifiersText(selectedDependency)}
                                                    </h5>
                                                }
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="2">
                                                <Label>Match</Label>
                                            </Col>
                                            <Col>
                                                {selectedDependency.editable
                                                    ?
                                                    <h5>
                                                        {dimensionsToMatchSelector}
                                                    </h5>
                                                    :
                                                    <h5>
                                                        {getMatchText(selectedDependency)}
                                                    </h5>
                                                }
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="2">
                                                <Label>Relationship:</Label>
                                            </Col>
                                            <Col>
                                    {selectedDependency.editable
                                        ?
                                        <div>
                                            {TargetRelationships()}
                                        </div>
                                        :
                                        <Label>{depRelationship}</Label>
                                    }
                                </Col>
                            </Row>
                            <Row>
                                <Col md="2">
                                    <Label>Use Fallback</Label>
                                </Col>
                                <Col >
                                    <Input
                                        type="checkbox"
                                        name="use_fallback"
                                        onChange={(e) => handleUseFallbackChange(e)}
                                        disabled={!selectedDependency.editable}
                                        checked={depUseFallback}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md="2">
                                    <Label>Sequence:</Label>
                                </Col>
                                <Col>
                                    <Switch checked={convertTextToBoolean(depIsSequence)}
                                        onChange={(e) => handleIsSequenceChangeBoolean(e)}
                                        disabled={!selectedDependency.editable} />
                                </Col>
                            </Row>
                            {targetIsMonetary() &&
                                <Row>
                                    <Col md="2">
                                        <Label>Precision:</Label>
                                    </Col>
                                    <Col>
                                        <Select
                                            value={getDecimalsOptions().filter(function (option) {
                                                return option.value === decimals;
                                            })
}
                                            onChange={handleDecimalsChange}
                                            options={getDecimalsOptions()}
                                        />
                                    </Col>
                                </Row>
                            }
                            {!hasExistenceRelationship(depRelationship) &&
                                <Row>
                                <Col>
                                        {hasMonetaryValueRelationship(depRelationship) &&
                                            <Row>
                                            <Col>
                                                <Row>
                                                    <Col md="2">
                                                        <Label>Amount</Label>
                                                    </Col>
                                                    <Col>
                                                        <Input type="number" value={depValue}
                                                            onChange={(e) => handleValueChange(e.currentTarget.value)}
                                                            disabled={!selectedDependency.editable} />
                                                    </Col>
                                                </Row>
                                            </Col>
                                            </Row>
                                        }
                                        {hasPercentValueRelationship(depRelationship) &&
                                            <Row>
                                                <Col>
                                                    <Row>
                                                        <Col md="2">
                                                            <Label>Percentage</Label>
                                                        </Col>
                                                        <Col>
                                                            <NumericFormat suffix="%" value={convertFloatTextToPercentText(depValue)}
                                                                customInput={Input}
                                                                onValueChange={(values, sourceInfo) => {
                                                                    handlePercentValueChange(values.floatValue);
                                                                }} />
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        }
                                        {hasBooleanValueRelationship(depRelationship) &&
                                            <Row>
                                                <Col>
                                                    <Row>
                                                        <Col md="2">
                                                            <Label>Value</Label>
                                                        </Col>
                                                        <Col>
                                                            <Switch checked={depBooleanValue ? depBooleanValue : false}
                                                                onChange={(e) => handleBooleanValueChange(e)}
                                                                disabled={!selectedDependency.editable} />
                                                        </Col>
                                                        <Col>
                                                            <Label className="ml-4" check>
                                                                <Input type="radio"
                                                                    name="boolValue"
                                                                    value={"TRUE"}
                                                                    onChange={(e) => handleBooleanValueChange_radio(e)}
                                                                    checked={depBooleanValue ? depBooleanValue : false}
                                                                /><b>True</b>
                                                            </Label>
                                                        </Col>
                                                        <Col>
                                                            <Label className="ml-4" check>
                                                                <Input type="radio"
                                                                    name="boolValue"
                                                                    value={"FALSE"}
                                                                    onChange={(e) => handleBooleanValueChange_radio(e)}
                                                                    checked={depBooleanValue ? !depBooleanValue : true}
                                                                /><b>False</b>
                                                            </Label>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        }
                                        {hasTestRelationship(depRelationship) &&
                                            <Row>
                                                <Col md="2">
                                                    <Label>Test:</Label>
                                                </Col>
                                                <Col>
                                                    <Input type="textarea" value={depTest}
                                                        onChange={(e) => handleTestChange(e.currentTarget.value)}
                                                        disabled={!selectedDependency.editable} />
                                                </Col>
                                            </Row>
                                        }
                                        {hasMonetaryValueRelationship(depRelationship) && depUseFallback &&
                                            <Row>
                                                    <Col md="2">
                                                        <Label>Fallback</Label>
                                                    </Col>
                                                    <Col>
                                                        <Input type="number" value={depFallback}
                                                            onChange={(e) => handleFallbackChange(e.currentTarget.value)}
                                                            disabled={!selectedDependency.editable} />
                                                    </Col>
                                            </Row>
                                        }
                                        {hasBooleanValueRelationship(depRelationship) && depUseFallback &&
                                            <Row>
                                                <Col md="2">
                                                    <Label>Fallback</Label>
                                                </Col>
                                                <Col>
                                                    <Switch checked={depBooleanFallback ? depBooleanFallback : false}
                                                        onChange={(e) => handleBooleanFallbackChange(e)}
                                                        disabled={!selectedDependency.editable} />
                                                </Col>
                                                <Col>
                                                    <Label className="ml-4" check>
                                                        <Input type="radio"
                                                            name="boolFallback"
                                                            value={"TRUE"}
                                                            onChange={(e) => handleBooleanFallbackChange_radio(e)}
                                                            checked={depBooleanFallback ? depBooleanFallback : false}
                                                        /><b>True</b>
                                                    </Label>
                                                </Col>
                                                <Col>
                                                    <Label className="ml-4" check>
                                                        <Input type="radio"
                                                            name="boolFallback"
                                                            value={"FALSE"}
                                                            onChange={(e) => handleBooleanFallbackChange_radio(e)}
                                                            checked={depBooleanFallback ? !depBooleanFallback: true}
                                                        /><b>False</b>
                                                    </Label>
                                                </Col>
                                            </Row>
                                        }
                                </Col>
                                </Row>
                            }
                            {selectedDependency.isRule &&
                                <Row>
                                    <Col md="2">
                                        <Label>Deprecation:</Label>
                                    </Col>
                                    <Col>
                                        {selectedDependency.editable
                                            ?
                                            <Select
                                                value={
                                                    getFiscalYears().filter(function (option) {
                                                        return option.value === getDeprecationYear(selectedDependency as EditableAccountDependency);
                                                    })
                                                }
                                                onChange={handleDeprecationYearChange}
                                                options={getFiscalYears()}
                                            />
                                            :
                                            <Label>{getDeprecateDateString()}</Label>
                                         }
                                    </Col>
                                </Row>
                            }
                        </div>
                        :
                            <h5>No Selection</h5>
                    }
                </Col>
            </Row>
        </React.Fragment>
    );
    /*
                                        <DatePicker
                                            name="dependencyDeprecationDate"
                                            selected={deprecationDate}
                                            dateFormat="MM/dd/yyyy"
                                            onChange={handleDependencyDateChange}
                                            className="red-border"
                                            minDate={dateHelper.convertToDate(selectedDependency.year + "-10-01")}
                                            maxDate={new Date()}
                                        />
    */
}
export default AccountDependenciesEditor;